import { useEffect, useMemo, useState } from "react";
import type React from "react";
import { useToast } from "@/hooks/use-toast";
import { useQueryClient, useMutation, useQuery } from "@tanstack/react-query";
import { apiRequest, parseErrorMessage } from "@/lib/queryClient";
import { Card } from "@/components/ui/card";
import { Activity, AlertCircle, AlertTriangle, ArrowDown, ArrowLeft, ArrowUp, ArrowUpDown, Award, BadgeCheck, BarChart3, BookOpen, Bot, Building2, Calendar, CalendarClock, CheckCircle2, ChevronDown, ChevronRight, ChevronsDown, ChevronsUp, ClipboardList, Clock, CreditCard, Crown, DollarSign, Download, ExternalLink, FileText, FileUp, FlaskConical, Gauge, Gift, Globe, Handshake, HardDrive, History, Lightbulb, Loader2, Lock, LogOut, Mail, Megaphone, Menu, MessageSquare, Minus, Pencil, Plus, RefreshCw, ScrollText, Shield, ShieldAlert, ShieldCheck, Snowflake, Sparkles, Star, Tag, Thermometer, Trash2, TrendingUp, UserCheck, UserCog, UserX, Users, Video, Zap } from "lucide-react";
import { GuideButton } from "@/components/guide-button";
import { GuideNudgeBanner } from "@/components/guide-nudge-banner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Link, useLocation, useSearch } from "wouter";
import { useTabQueryParam } from "@/hooks/use-tab-query-param";
import { FOUNDER_TIERS, FOUNDER_TIER_FREE_ROOMS, FOUNDER_TIER_PRICE_USD, SUBSCRIPTION_STATUSES } from "@shared/schema";
import type { AppUser, AppUserSubscriptionStatusEvent, AppUserWithStatusEvent, EngagementBucket, ExplainerVideo, ExplainerVideoHistory, FeatureRequest, FounderIntent, FounderIntentPaidEvent, FounderIntentWithPaidEvent, FounderTier, Lead, SubscriptionStatus, UserActivityStats, UserSectionActivity } from "@shared/schema";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Switch } from "@/components/ui/switch";
import { EXPLAINER_VIDEO_KEYS } from "@/lib/explainer-video-keys";
import { MenuTierLocksPanel } from "@/components/menu-tier-locks-panel";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { usePageTitle } from "@/hooks/use-page-title";
import { AdminCountContext, AdminNavSection } from "@/pages/admin/types";
import { downloadCSV, formatDate, formatStage, formatUsd, planLabel, planVariant, subStatusSourceLabel } from "@/pages/admin/helpers";
import { AdminPartnerMessagesPanel } from "@/pages/admin/AdminPartnerMessagesPanel";
import { AffiliatePayoutsPanel } from "@/pages/admin/AffiliatePayoutsPanel";
import { AuditLogHealthBanner } from "@/pages/admin/AuditLogHealthBanner";
import { AiCategorySuggestionsPanel } from "@/pages/admin/AiCategorySuggestionsPanel";
import { AllPartnersPanel } from "@/pages/admin/AllPartnersPanel";
import { ApifyUsagePanel } from "@/pages/admin/ApifyUsagePanel";
import { BetaFeaturesTab } from "@/pages/admin/BetaFeaturesTab";
import { BillingErrorsPanel } from "@/pages/admin/BillingErrorsPanel";
import { FounderOveragesPanel } from "@/pages/admin/FounderOveragesPanel";
import { BillingV3Panel } from "@/pages/admin/BillingV3Panel";
import { FunnelPanel } from "@/pages/admin/FunnelPanel";
import { BlogImportPanel } from "@/pages/admin/BlogImportPanel";
import { AppFlagsPanel } from "@/pages/admin/AppFlagsPanel";
import { ChangeLogPanel } from "@/pages/admin/ChangeLogPanel";
import { CityAiAnalysesPanel } from "@/pages/admin/CityAiAnalysesPanel";
import { ConferencePromoBannerPanel } from "@/pages/admin/ConferencePromoBannerPanel";
import { DemoSandboxPanel } from "@/pages/admin/DemoSandboxPanel";
import { CrispConversationsDialog } from "@/pages/admin/CrispConversationsDialog";
import { UserActivityDrilldownDialog } from "@/pages/admin/UserActivityDrilldownDialog";
import { ElevenlabsUsagePanel } from "@/pages/admin/ElevenlabsUsagePanel";
import { EncryptionAuditPanel } from "@/pages/admin/EncryptionAuditPanel";
import { FoundingAccessDatePanel } from "@/pages/admin/FoundingAccessDatePanel";
import { MessagingOutagePanel } from "@/pages/admin/MessagingOutagePanel";
import { AiReplyVoicePanel } from "@/pages/admin/AiReplyVoicePanel";
import { ChurnRiskAlertsPanel } from "@/pages/admin/ChurnRiskAlertsPanel";
import { GeminiUsagePanel } from "@/pages/admin/GeminiUsagePanel";
import { ImpersonationLogPanel } from "@/pages/admin/ImpersonationLogPanel";
import { AdminUsersPanel } from "@/pages/admin/AdminUsersPanel";
import { LendersBrokersPanel } from "@/pages/admin/LendersBrokersPanel";
import { MarketResearchPdfImportPanel } from "@/pages/admin/MarketResearchPdfImportPanel";
import { MarketAutoPullAdmin } from "@/pages/admin/MarketAutoPullAdmin";
import { MarketplaceFeesPanel } from "@/pages/admin/MarketplaceFeesPanel";
import { MessageAttachmentStoragePanel } from "@/pages/admin/MessageAttachmentStoragePanel";
import { MessagesPendingReconcileHealthPanel } from "@/pages/admin/MessagesPendingReconcileHealthPanel";
import { PadsplitSendHealthPanel } from "@/pages/admin/PadsplitSendHealthPanel";
import { RoomHarvestPanel } from "@/pages/admin/RoomHarvestPanel";
import { AuditLogHealthPanel } from "@/pages/admin/AuditLogHealthPanel";
import { OpsDocsPanel } from "@/pages/admin/OpsDocsPanel";
import { PartnerAgreementSubmissionsPanel } from "@/pages/admin/PartnerAgreementSubmissionsPanel";
import { PartnerApplicationNotificationSettingsPanel } from "@/pages/admin/PartnerApplicationNotificationSettingsPanel";
import { PartnerApplicationsPanel } from "@/pages/admin/PartnerApplicationsPanel";
import { PaymentInstructionsPanel } from "@/pages/admin/PaymentInstructionsPanel";
import { PrivacyPolicyNotifyPanel } from "@/pages/admin/PrivacyPolicyNotifyPanel";
import { TermsPrivacyNoticePanel } from "@/pages/admin/TermsPrivacyNoticePanel";
import { PromoCodesTab } from "@/pages/admin/PromoCodesTab";
import { PulseDigestHealthPanel } from "@/pages/admin/PulseDigestHealthPanel";
import { PulseFeedbackPanel } from "@/pages/admin/PulseFeedbackPanel";
import { RealtorPartnersPanel } from "@/pages/admin/RealtorPartnersPanel";
import { ReferHostAccrualsPanel } from "@/pages/admin/ReferHostAccrualsPanel";
import { ReferHostReferrersPanel } from "@/pages/admin/ReferHostReferrersPanel";
import { ReferHostStatementsPanel } from "@/pages/admin/ReferHostStatementsPanel";
import { ReferralCommissionsPanel } from "@/pages/admin/ReferralCommissionsPanel";
import { RejectedTasksPanel } from "@/pages/admin/RejectedTasksPanel";
import { SaleListingsAdminPanel } from "@/pages/admin/SaleListingsAdminPanel";
import { SampleBuyDealsPanel } from "@/pages/admin/SampleBuyDealsPanel";
import { SecretScanPanel } from "@/pages/admin/SecretScanPanel";
import { StripeWipeTestArtifactsPanel } from "@/pages/admin/StripeWipeTestArtifactsPanel";
import { ThermostatPollHealthPanel } from "@/pages/admin/ThermostatPollHealthPanel";
import { TosNotificationsPanel } from "@/pages/admin/TosNotificationsPanel";
import { TosVersionsPanel } from "@/pages/admin/TosVersionsPanel";
import { VendorEmailResetPanel } from "@/pages/admin/VendorEmailResetPanel";
import { VendorTotpResetPanel } from "@/pages/admin/VendorTotpResetPanel";
import { MemberMergePanel } from "@/pages/admin/MemberMergePanel";
import { AssistantPasswordRevealPanel } from "@/pages/admin/AssistantPasswordRevealPanel";
import { VoiceAudioCachePanel } from "@/pages/admin/VoiceAudioCachePanel";
import { WholesalerPartnersPanel } from "@/pages/admin/WholesalerPartnersPanel";

export { MessageAttachmentStoragePanel, ChangeLogPanel };

function VideoEditHistory({ videoId }: { videoId: number }) {
  const historyQuery = useQuery<ExplainerVideoHistory[]>({
    queryKey: ["/api/admin/explainer-videos", videoId, "history"],
    queryFn: async () => {
      const res = await fetch(`/api/admin/explainer-videos/${videoId}/history`);
      if (!res.ok) throw new Error("Failed to fetch history");
      return res.json();
    },
  });

  if (historyQuery.isLoading) {
    return <div className="text-xs text-muted-foreground py-2">Loading history…</div>;
  }
  if (!historyQuery.data || historyQuery.data.length === 0) {
    return null;
  }

  return (
    <div className="border-t pt-4 mt-2" data-testid="video-edit-history">
      <h4 className="text-sm font-medium flex items-center gap-1.5 mb-3">
        <History className="h-3.5 w-3.5" />
        Edit History
      </h4>
      <div className="space-y-2 max-h-48 overflow-y-auto pr-1">
        {historyQuery.data.map((entry) => (
          <div key={entry.id} className="text-xs border rounded p-2 bg-muted/30" data-testid={`video-history-entry-${entry.id}`}>
            <div className="flex items-center justify-between gap-2 mb-1">
              <span className="font-medium truncate">{entry.title}</span>
              <span className="text-muted-foreground whitespace-nowrap shrink-0">
                {entry.changedBy ? `${entry.changedBy} · ` : ""}
                {new Date(entry.changedAt).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" })}
              </span>
            </div>
            <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-muted-foreground">
              <span>Status: <span className="text-foreground">{entry.status}</span></span>
              {entry.embedUrl ? (
                <span>Loom: <a href={entry.embedUrl} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">link</a></span>
              ) : <span>Loom: —</span>}
              {entry.youtubeUrl ? (
                <span>YouTube: <a href={entry.youtubeUrl} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">link</a></span>
              ) : <span>YouTube: —</span>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function AdminLogin({ onLogin }: { onLogin: () => void }) {
  const [password, setPassword] = useState("");
  const [identity, setIdentity] = useState(() => {
    if (typeof window === "undefined") return "";
    return window.localStorage.getItem("padpulse_admin_identity") || "";
  });
  const { toast } = useToast();

  const loginMutation = useMutation({
    mutationFn: async (vars: { pw: string; identity: string }) => {
      const res = await fetch("/api/admin/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password: vars.pw, identity: vars.identity }),
        credentials: "include",
      });
      if (!res.ok) {
        const text = await res.text();
        throw new Error(`${res.status}: ${text}`);
      }
      return res.json();
    },
    onSuccess: () => {
      if (typeof window !== "undefined") {
        if (identity.trim()) {
          window.localStorage.setItem("padpulse_admin_identity", identity.trim());
        } else {
          window.localStorage.removeItem("padpulse_admin_identity");
        }
      }
      onLogin();
    },
    onError: (err: unknown) => {
      // 401 = bad credentials → always the generic copy (the server never
      // reveals whether the email or password was wrong). 429 = rate limited
      // or locked out. For 5xx the response includes an errorId we want to
      // surface so support can trace the incident.
      const isAuthError = err instanceof Error && /^401:/.test(err.message);
      const isRateLimited = err instanceof Error && /^429:/.test(err.message);
      toast({
        title: "Access denied",
        description: isAuthError
          ? "Invalid credentials."
          : isRateLimited
            ? parseErrorMessage(err, "Too many attempts. Please wait and try again.")
            : parseErrorMessage(err, "Couldn't sign in. Please try again."),
        variant: "destructive",
      });
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!password.trim()) return;
    const trimmed = identity.trim();
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!trimmed || !emailRegex.test(trimmed)) {
      toast({
        title: "Admin email required",
        description: "Enter the email of your admin account.",
        variant: "destructive",
      });
      return;
    }
    loginMutation.mutate({ pw: password, identity: trimmed });
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <Card className="w-full max-w-sm p-8">
        <div className="flex flex-col items-center text-center mb-6">
          <div className="flex h-12 w-12 items-center justify-center rounded-md bg-primary/10 mb-4">
            <Lock className="h-6 w-6 text-primary" />
          </div>
          <h1 className="text-xl font-bold" data-testid="text-admin-login-title">Admin Access</h1>
          <p className="text-sm text-muted-foreground mt-1">Sign in with your admin email and password</p>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-1">
            <Input
              type="email"
              placeholder="Admin email"
              value={identity}
              onChange={(e) => setIdentity(e.target.value)}
              autoComplete="email"
              required
              data-testid="input-admin-identity"
            />
          </div>
          <Input
            type="password"
            placeholder="Admin password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            autoFocus
            data-testid="input-admin-password"
          />
          <Button
            type="submit"
            className="w-full"
            disabled={loginMutation.isPending}
            data-testid="button-admin-login"
          >
            {loginMutation.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              "Sign In"
            )}
          </Button>
        </form>
        <div className="mt-4 text-center">
          <Link href="/">
            <Button variant="ghost" size="sm" data-testid="button-back-home">
              <ArrowLeft className="h-4 w-4 mr-1" />
              Back to site
            </Button>
          </Link>
        </div>
      </Card>
    </div>
  );
}

const ADMIN_NAV_SECTIONS: AdminNavSection[] = [
  {
    label: "Growth & Acquisition",
    icon: Zap,
    items: [
      { value: "funnel", label: "Funnel", icon: TrendingUp },
      { value: "leads", label: "Leads", icon: Users, getCount: (c) => c.leads },
      { value: "conference-leads", label: "Conference Leads", icon: CalendarClock, href: "/app/admin/conference-leads" },
      { value: "features", label: "Feature Requests", icon: Lightbulb, getCount: (c) => c.featureReqs },
      { value: "founder-intents", label: "Founder Signups", icon: Crown, getCount: (c) => c.founderIntentsAll },
    ],
  },
  {
    label: "Users & Access",
    icon: UserCheck,
    items: [
      { value: "app-users", label: "App Users", icon: UserCheck, getCount: (c) => c.appUsersList },
      { value: "admin-users", label: "Admin Users", icon: ShieldCheck },
      { value: "impersonation-log", label: "Impersonation Log", icon: Shield },
      { value: "vendor-2fa", label: "Vendor 2FA Resets", icon: ShieldAlert },
      { value: "vendor-email-reset", label: "Vendor Email Resets", icon: Mail },
      { value: "assistant-passwords", label: "Assistant Passwords", icon: Bot },
      { value: "member-merge", label: "Member Merge", icon: Users },
      { value: "beta-features", label: "Beta Features", icon: FlaskConical },
      { value: "menu-tier-locks", label: "Menu Tier Locks", icon: Lock },
    ],
  },
  {
    label: "Compliance & Legal",
    icon: Shield,
    items: [
      { value: "tos-versions", label: "ToS Versions", icon: FileText },
      { value: "tos-notifications", label: "ToS Notifications", icon: FileText },
      { value: "privacy-policy-notify", label: "Privacy Policy Notify", icon: ShieldAlert },
      { value: "terms-privacy-notice", label: "Terms & Privacy Notice", icon: ShieldAlert },
    ],
  },
  {
    label: "Billing & Finance",
    icon: CreditCard,
    items: [
      { value: "billing-v3", label: "Billing v3", icon: CreditCard },
      { value: "billing-errors", label: "Billing Errors", icon: AlertCircle },
      { value: "founder-overages", label: "Founder Overages", icon: Crown },
      { value: "promo-codes", label: "Promo Codes", icon: Tag },
      { value: "payment-instructions", label: "Payment Instructions", icon: CreditCard },
      { value: "referral-commissions", label: "Referral Commissions", icon: DollarSign },
      { value: "stripe-wipe", label: "Wipe Stripe Test Data", icon: Trash2 },
      { value: "rejected-tasks", label: "Rejected Tasks", icon: ShieldAlert },
      { value: "vendor-marketplace-fees", label: "Vendor Marketplace Fees", icon: DollarSign },
    ],
  },
  {
    label: "Partners",
    icon: Handshake,
    items: [
      { value: "partners-applications", label: "Applications", icon: ClipboardList },
      { value: "partners-agreements", label: "Agreement Submissions", icon: FileText },
      { value: "partners-all", label: "All Partners", icon: Handshake },
      { value: "partners-realtors", label: "Realtors", icon: BadgeCheck },
      { value: "partners-wholesalers", label: "Wholesalers", icon: RefreshCw },
      { value: "lenders-brokers", label: "Lenders & Brokers", icon: Building2 },
      { value: "partners-messages", label: "Partner Messages", icon: MessageSquare },
      { value: "partners-application-alerts", label: "Application Alerts", icon: Mail },
    ],
  },
  {
    label: "Refer-a-Host Program",
    icon: Gift,
    items: [
      { value: "refer-host-referrers", label: "Referrers", icon: Award },
      { value: "refer-host-statements", label: "Quarterly Statements", icon: Calendar },
      { value: "refer-host-accruals", label: "Accruals Log", icon: TrendingUp },
      { value: "affiliate-payouts", label: "Affiliate Payouts", icon: DollarSign },
    ],
  },
  {
    label: "Content & Marketing",
    icon: Megaphone,
    // Admin Access Layer — the marketing capabilities make these tabs visible
    // to non-super admins; every untagged tab in this file is super-admin-only.
    items: [
      { value: "blog", label: "Blog", icon: FileText },
      { value: "explainer-videos", label: "Explainer Videos", icon: Video, capability: "view_marketing_materials" },
      { value: "sample-buy-deals", label: "Sample Buy Deals", icon: Tag, capability: "view_marketing_materials" },
      { value: "sale-listings", label: "Sell Listings", icon: Tag, capability: "view_marketing_materials" },
      { value: "city-ai-analyses", label: "City AI Analyses", icon: Sparkles, capability: "view_marketing_materials" },
      { value: "market-research-pdf", label: "Market PDF Import", icon: FileUp, capability: "upload_marketing_data" },
    ],
  },
  {
    label: "System Health & Monitoring",
    icon: Gauge,
    items: [
      { value: "gemini-usage", label: "Gemini Usage", icon: Sparkles },
      { value: "apify-usage", label: "Apify Usage", icon: Globe },
      { value: "elevenlabs-usage", label: "ElevenLabs Voice Usage", icon: Sparkles },
      { value: "voice-audio-cache", label: "Voice Briefing Cache", icon: HardDrive },
      { value: "pulse-digest", label: "Pulse Digest", icon: Mail },
      { value: "pulse-feedback", label: "Pulse Feedback", icon: MessageSquare },
      { value: "ai-category-suggestions", label: "AI Category Suggestions", icon: Sparkles },
      { value: "thermostat-poll-health", label: "Thermostat Polling", icon: Thermometer },
      { value: "messages-pending-reconcile", label: "Messages Pending Reconcile", icon: MessageSquare },
      { value: "padsplit-send-health", label: "PadSplit Send Health", icon: MessageSquare },
      { value: "room-harvest", label: "Room Harvest", icon: Clock },
      { value: "audit-log-health", label: "Audit Log Health", icon: ShieldAlert },
      { value: "message-attachments", label: "Message Attachment Storage", icon: HardDrive },
      { value: "changelog", label: "Change Log", icon: ScrollText },
    ],
  },
  {
    label: "Security & Audit",
    icon: ShieldAlert,
    items: [
      { value: "secret-scan", label: "Secret Scan", icon: ShieldAlert },
      { value: "encryption-audit", label: "Encryption Audit", icon: Lock },
    ],
  },
  {
    label: "Platform Settings",
    icon: BookOpen,
    items: [
      { value: "app-flags", label: "App Flags", icon: Zap },
      { value: "important-links", label: "Important Links", icon: BookOpen },
      { value: "conference-promo", label: "Conference Promo Banner", icon: Megaphone },
      { value: "founding-access-date", label: "Founder Circle Launch Date", icon: Crown },
      { value: "messaging-outage", label: "Messaging Outage", icon: MessageSquare },
      { value: "ai-reply-voice", label: "AI Reply Voice", icon: Globe },
      { value: "churn-risk-alerts", label: "Churn-Risk Alerts", icon: AlertTriangle },
      { value: "demo-sandbox", label: "Demo Sandbox", icon: Snowflake },
    ],
  },
];

const VALID_ADMIN_TABS: readonly string[] = ADMIN_NAV_SECTIONS.flatMap(
  (section) => section.items.map((item) => item.value),
);

function LeadArchiveButton({ lead, onDone }: { lead: Lead; onDone: () => void }) {
  const { toast } = useToast();
  const isArchived = !!lead.archivedAt;

  const mutation = useMutation({
    mutationFn: () => apiRequest("POST", `/api/leads/${lead.id}/${isArchived ? "restore" : "archive"}`),
    onSuccess: () => {
      toast({ title: isArchived ? "Lead restored" : "Lead archived", description: isArchived ? `${lead.email} is back in the active list.` : `${lead.email} has been archived.` });
      onDone();
    },
    onError: (err) => {
      toast({ title: "Action failed", description: parseErrorMessage(err, "Something went wrong. Please try again."), variant: "destructive" });
    },
  });

  return (
    <Button
      variant="ghost"
      size="sm"
      data-testid={`button-${isArchived ? "restore" : "archive"}-lead-${lead.id}`}
      disabled={mutation.isPending}
      onClick={() => mutation.mutate()}
      className={isArchived ? "text-emerald-700 hover:text-emerald-800" : "text-muted-foreground hover:text-destructive"}
    >
      {mutation.isPending ? (
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
      ) : isArchived ? (
        <RefreshCw className="h-3.5 w-3.5 mr-1" />
      ) : (
        <Trash2 className="h-3.5 w-3.5 mr-1" />
      )}
      {isArchived ? "Restore" : "Archive"}
    </Button>
  );
}

function LeadInviteButton({ lead, withinCooldown, invitedAt }: {
  lead: Lead;
  withinCooldown: boolean;
  invitedAt: Date | null;
}) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const inviteMutation = useMutation({
    mutationFn: () => apiRequest("POST", `/api/leads/${lead.id}/invite`),
    onSuccess: () => {
      toast({ title: "Invite sent", description: `Invitation emailed to ${lead.email}.` });
      queryClient.invalidateQueries({ queryKey: ["/api/leads"] });
    },
    onError: (err) => {
      toast({ title: "Failed to send invite", description: parseErrorMessage(err, "Something went wrong. Please try again."), variant: "destructive" });
    },
  });

  if (withinCooldown && invitedAt) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <span>
              <Button
                variant="outline"
                size="sm"
                disabled
                data-testid={`button-invite-lead-${lead.id}`}
                className="text-emerald-700 border-emerald-300 bg-emerald-50 cursor-not-allowed opacity-80"
              >
                <CheckCircle2 className="h-3.5 w-3.5 mr-1.5" />
                Invited ✓ {formatDate(invitedAt.toISOString())}
              </Button>
            </span>
          </TooltipTrigger>
          <TooltipContent>Last invited: {invitedAt.toLocaleString()}</TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return (
    <div className="flex flex-col items-center gap-0.5">
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              data-testid={`button-invite-lead-${lead.id}`}
              disabled={inviteMutation.isPending}
              onClick={() => inviteMutation.mutate()}
            >
              {inviteMutation.isPending ? (
                <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
              ) : (
                <Mail className="h-3.5 w-3.5 mr-1.5" />
              )}
              Invite to Sign Up
            </Button>
          </TooltipTrigger>
          {invitedAt && (
            <TooltipContent>Last invited: {invitedAt.toLocaleString()}</TooltipContent>
          )}
        </Tooltip>
      </TooltipProvider>
      {invitedAt && (
        <span className="text-[11px] text-muted-foreground leading-none" data-testid={`text-lead-invited-at-${lead.id}`}>
          Invited {formatDate(invitedAt.toISOString())}
        </span>
      )}
    </div>
  );
}

function AdminDashboard({ onLogout, adminIdentity }: { onLogout: () => void; adminIdentity: string | null }) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [pathname, navigate] = useLocation();
  const search = useSearch();

  // Use shared hook for reactive URL read; custom setter preserves admin-specific
  // param-stripping logic (runId for secret-scan, release/releaseSha for changelog).
  // Admin Access Layer — fresh capability payload for nav gating. Anything
  // short of an explicit `superAdmin: false` (loading, error, legacy shape)
  // renders the full super-admin nav; the server enforces every route
  // regardless, so this only controls visibility.
  const adminMeQuery = useQuery<{ email: string; superAdmin: boolean; permissions: string[] }>({
    queryKey: ["/api/admin/me"],
  });
  const adminMe = adminMeQuery.data;
  const isSuperAdminView = adminMe?.superAdmin !== false;
  const adminPermissions = Array.isArray(adminMe?.permissions) ? adminMe.permissions : [];
  const visibleNavSections = ADMIN_NAV_SECTIONS
    .map((section) => ({
      ...section,
      items: section.items.filter(
        (item) => isSuperAdminView || (item.capability ? adminPermissions.includes(item.capability) : false),
      ),
    }))
    .filter((section) => section.items.length > 0);
  const visibleTabs: readonly string[] = visibleNavSections.flatMap((section) =>
    section.items.map((item) => item.value),
  );
  // Super-only data (sidebar counts + aggregate tables) waits for /me and is
  // skipped entirely for capability-tier admins so they don't spray 403s.
  const superDataEnabled = adminMeQuery.isSuccess && isSuperAdminView;
  const canManageMarketing = isSuperAdminView || adminPermissions.includes("manage_marketing_materials");

  // tab-allow-list-exempt: VALID_ADMIN_TABS clamping is applied inline below
  // because the admin tab list is shared with a custom setter that strips
  // tab-specific query params.
  const [_rawTab] = useTabQueryParam("tab", "leads", { replace: true });
  // Clamp order: unknown tabs -> "leads" (pre-existing behavior); known but
  // not-permitted tabs -> the first tab this admin can see.
  const activeTab: string = visibleTabs.includes(_rawTab)
    ? _rawTab
    : (VALID_ADMIN_TABS as readonly string[]).includes(_rawTab)
      ? (visibleTabs[0] ?? "leads")
      : (visibleTabs.includes("leads") ? "leads" : (visibleTabs[0] ?? "leads"));

  function setActiveTab(tab: string) {
    const next = new URLSearchParams(search);
    if (tab === "leads") {
      next.delete("tab");
    } else {
      next.set("tab", tab);
    }
    // Task #876 — strip section-specific deep-link params when leaving their tab
    if (tab !== "secret-scan") {
      next.delete("runId");
    }
    if (tab !== "changelog") {
      next.delete("release");
      next.delete("releaseSha");
    }
    if (tab !== "explainer-videos") {
      next.delete("videoStatus");
      next.delete("videoSection");
      next.delete("videoSearch");
    }
    if (tab !== "app-users") {
      next.delete("focusUser");
    }
    const qs = next.toString();
    navigate(qs ? `${pathname}?${qs}` : pathname, { replace: true });
  }

  // ── Admin sidebar state ──────────────────────────────────────────────────
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);

  const [expandedSections, setExpandedSections] = useState<Set<string>>(() => {
    const activeSection = ADMIN_NAV_SECTIONS.find((s) =>
      s.items.some((item) => item.value === _rawTab),
    )?.label;
    try {
      const stored = localStorage.getItem("admin-sidebar-expanded-v1");
      if (stored) {
        const arr = JSON.parse(stored) as string[];
        const s = new Set(arr);
        if (activeSection) s.add(activeSection);
        return s;
      }
    } catch {}
    return new Set(activeSection ? [activeSection] : ["Growth & Acquisition"]);
  });

  const allSectionLabels = visibleNavSections.map((s) => s.label);
  const allExpanded = allSectionLabels.every((l) => expandedSections.has(l));
  const allCollapsed = expandedSections.size === 0;

  const collapseAll = () => setExpandedSections(new Set());
  const expandAll = () => setExpandedSections(new Set(allSectionLabels));

  // Auto-expand the section containing the active tab (deep-link / tab change)
  useEffect(() => {
    const activeSection = visibleNavSections.find((s) =>
      s.items.some((item) => item.value === activeTab),
    )?.label;
    if (activeSection) {
      setExpandedSections((prev) => {
        if (prev.has(activeSection)) return prev;
        const next = new Set(prev);
        next.add(activeSection);
        return next;
      });
    }
  }, [activeTab]);

  // Persist expanded-section state to localStorage
  useEffect(() => {
    try {
      localStorage.setItem(
        "admin-sidebar-expanded-v1",
        JSON.stringify([...expandedSections]),
      );
    } catch {}
  }, [expandedSections]);

  function toggleSection(label: string) {
    setExpandedSections((prev) => {
      const next = new Set(prev);
      if (next.has(label)) next.delete(label);
      else next.add(label);
      return next;
    });
  }
  // ────────────────────────────────────────────────────────────────────────

  const [showArchivedLeads, setShowArchivedLeads] = useState(false);

  const leadsQuery = useQuery<Lead[]>({
    enabled: superDataEnabled,
    queryKey: ["/api/leads", showArchivedLeads ? "archived" : "active"],
    queryFn: async () => {
      const url = showArchivedLeads ? "/api/leads?status=archived" : "/api/leads";
      const res = await fetch(url, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to fetch leads");
      return res.json();
    },
  });

  const featureRequestsQuery = useQuery<FeatureRequest[]>({
    enabled: superDataEnabled,
    queryKey: ["/api/feature-requests"],
  });

  const appUsersQuery = useQuery<AppUserWithStatusEvent[]>({
    enabled: superDataEnabled,
    queryKey: ["/api/admin/app-users"],
  });

  // Task #2856: batched per-host operator-unread support-chat counts so the
  // Users table can flag who has waiting Crisp chats without an N+1 per row.
  const crispUnreadQuery = useQuery<{ configured: boolean; counts: Record<string, number> }>({
    enabled: superDataEnabled,
    queryKey: ["/api/admin/crisp/unread-counts"],
    staleTime: 30_000,
    refetchInterval: 60_000,
  });
  const crispUnreadCounts = crispUnreadQuery.data?.counts ?? {};

  const explainerVideosQuery = useQuery<ExplainerVideo[]>({
    queryKey: ["/api/admin/explainer-videos"],
    queryFn: async () => {
      const res = await fetch("/api/admin/explainer-videos");
      if (!res.ok) throw new Error("Failed to fetch");
      return res.json();
    },
  });

  const founderTierClicksQuery = useQuery<Record<FounderTier, number>>({
    enabled: superDataEnabled,
    queryKey: ["/api/admin/founder-tier-clicks"],
  });

  const founderIntentsQuery = useQuery<FounderIntentWithPaidEvent[]>({
    enabled: superDataEnabled,
    queryKey: ["/api/admin/founder-intents"],
    // Task #3243: Mercury AR webhooks mark founder intents paid server-side
    // without touching the client cache. Poll while the Founder Signups tab is
    // open so webhook-driven paid/unpaid changes appear live without a manual
    // refresh; pause polling on other tabs to avoid needless requests.
    refetchInterval: activeTab === "founder-intents" ? 15_000 : false,
  });

  const [founderTierFilter, setFounderTierFilter] = useState<string>("all");
  // Task #3243: filter the founder signups list by payment status.
  const [founderPaidFilter, setFounderPaidFilter] = useState<"all" | "paid" | "unpaid">("all");
  // Task #2026: default to registration order (ascending) per spec.
  const [founderSortDir, setFounderSortDir] = useState<"desc" | "asc">("asc");
  const [founderRefDrafts, setFounderRefDrafts] = useState<Record<number, string>>({});
  // Task #2027: confirm dialog before granting access so the admin sees
  // exactly which tier (free-room count + price) is about to be applied.
  const [founderToActivate, setFounderToActivate] = useState<FounderIntent | null>(null);

  const activateFounderMutation = useMutation({
    mutationFn: async (id: number) => {
      const res = await apiRequest("POST", `/api/admin/founder-intents/${id}/activate`, {});
      return res.json() as Promise<{ ok: boolean; alreadyActivated: boolean; message?: string }>;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/founder-intents"] });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/app-users"] });
      if (data.alreadyActivated) {
        toast({ title: "Access already granted", description: data.message || "This founder already has access." });
      } else {
        toast({ title: "Access granted", description: "Account created and welcome email sent." });
      }
    },
    onError: (err: unknown) => {
      toast({
        title: "Could not grant access",
        description: parseErrorMessage(err, "Please try again."),
        variant: "destructive",
      });
    },
  });

  // Task #2026: toggle paid/unpaid on a founder intent.
  const markFounderPaidMutation = useMutation({
    mutationFn: async (input: { id: number; paid: boolean }) => {
      const res = await apiRequest("POST", `/api/admin/founder-intents/${input.id}/mark-paid`, { paid: input.paid });
      return res.json();
    },
    onSuccess: (_d, vars) => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/founder-intents"] });
      toast({ title: vars.paid ? "Marked as paid" : "Marked as unpaid" });
    },
    onError: (err: unknown) => {
      toast({
        title: "Could not update payment",
        description: parseErrorMessage(err, "Please try again."),
        variant: "destructive",
      });
    },
  });

  // Task #2026: save editable Mercury / Stripe transaction reference.
  const updateFounderReferenceMutation = useMutation({
    mutationFn: async (input: { id: number; paymentReference: string | null }) => {
      const res = await apiRequest("PATCH", `/api/admin/founder-intents/${input.id}`, {
        paymentReference: input.paymentReference,
      });
      return res.json();
    },
    onSuccess: (_d, vars) => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/founder-intents"] });
      setFounderRefDrafts((prev) => {
        const next = { ...prev };
        delete next[vars.id];
        return next;
      });
      toast({ title: "Transaction link saved" });
    },
    onError: (err: unknown) => {
      toast({
        title: "Could not save link",
        description: parseErrorMessage(err, "Please try again."),
        variant: "destructive",
      });
    },
  });

  const [videoDialog, setVideoDialog] = useState<{ open: boolean; editing: ExplainerVideo | null }>({ open: false, editing: null });
  const [videoForm, setVideoForm] = useState({ pageKey: "", title: "", embedUrl: "", youtubeUrl: "", status: "draft" as "draft" | "pending" | "final" });
  // Distinct registry section groups (preserve registry order) for the section filter dropdown.
  const videoSectionGroups = useMemo(() => {
    const seen = new Set<string>();
    const groups: string[] = [];
    for (const k of EXPLAINER_VIDEO_KEYS) {
      if (!seen.has(k.group)) {
        seen.add(k.group);
        groups.push(k.group);
      }
    }
    return groups;
  }, []);
  // Filter state is URL-backed (replace mode) so admins can bookmark/share a view.
  const [videoStatusFilterRaw, setVideoStatusFilter] = useTabQueryParam("videoStatus", "all", {
    replace: true,
    tabs: ["all", "draft", "pending", "final"],
  });
  const videoStatusFilter = videoStatusFilterRaw as "all" | "draft" | "pending" | "final";
  const [videoSectionFilter, setVideoSectionFilter] = useTabQueryParam("videoSection", "all", {
    replace: true,
    tabs: ["all", ...videoSectionGroups],
  });
  // tab-allow-list-exempt: free-text search has no fixed value set to clamp against
  const [videoSearch, setVideoSearch] = useTabQueryParam("videoSearch", "", { replace: true });
  const [videoSort, setVideoSort] = useState<{ column: "status" | "updatedAt"; direction: "asc" | "desc" }>({ column: "updatedAt", direction: "desc" });

  const createVideoMutation = useMutation({
    mutationFn: async (data: { pageKey: string; title: string; embedUrl: string; youtubeUrl: string; status: string }) => {
      await apiRequest("POST", "/api/admin/explainer-videos", data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/explainer-videos"] });
      setVideoDialog({ open: false, editing: null });
      setVideoForm({ pageKey: "", title: "", embedUrl: "", youtubeUrl: "", status: "draft" });
      toast({ title: "Video saved", description: "Explainer video has been saved." });
    },
    onError: (err: unknown) => {
      toast({
        title: "Failed to save video",
        description: parseErrorMessage(err, "Could not save this explainer video."),
        variant: "destructive",
      });
    },
  });

  const updateVideoMutation = useMutation({
    mutationFn: async ({ id, data }: { id: number; data: { pageKey: string; title: string; embedUrl: string; youtubeUrl: string; status: string } }) => {
      await apiRequest("PATCH", `/api/admin/explainer-videos/${id}`, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/explainer-videos"] });
      setVideoDialog({ open: false, editing: null });
      setVideoForm({ pageKey: "", title: "", embedUrl: "", youtubeUrl: "", status: "draft" });
      toast({ title: "Video updated", description: "Explainer video has been updated." });
    },
    onError: (err: unknown) => {
      toast({
        title: "Failed to update video",
        description: parseErrorMessage(err, "Could not update this explainer video."),
        variant: "destructive",
      });
    },
  });

  const patchVideoStatusMutation = useMutation({
    mutationFn: async ({ id, status }: { id: number; status: string }) => {
      await apiRequest("PATCH", `/api/admin/explainer-videos/${id}`, { status });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/explainer-videos"] });
    },
    onError: (err: unknown) => {
      toast({
        title: "Failed to update video status",
        description: parseErrorMessage(err, "Could not update video status."),
        variant: "destructive",
      });
    },
  });

  const deleteVideoMutation = useMutation({
    mutationFn: async (id: number) => {
      await apiRequest("DELETE", `/api/admin/explainer-videos/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/explainer-videos"] });
      toast({ title: "Video deleted", description: "Explainer video has been removed." });
    },
    onError: (err: unknown) => {
      toast({
        title: "Failed to delete video",
        description: parseErrorMessage(err, "Could not delete this explainer video."),
        variant: "destructive",
      });
    },
  });

  const bulkQueueDraftsMutation = useMutation({
    mutationFn: async (keys: Array<{ key: string; label: string }>) => {
      const res = await apiRequest("POST", "/api/admin/explainer-videos/bulk-queue-drafts", { keys });
      return (await res.json()) as { created: number; skipped: number };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/explainer-videos"] });
      toast({ title: "Drafts queued", description: `${data.created} draft${data.created === 1 ? "" : "s"} created${data.skipped ? `, ${data.skipped} already configured` : ""}.` });
    },
    onError: (err: unknown) => {
      toast({
        title: "Failed to queue drafts",
        description: parseErrorMessage(err, "Could not create draft video entries."),
        variant: "destructive",
      });
    },
  });

  function openCreateVideo(prefilledKey?: string, prefilledTitle?: string) {
    setVideoForm({ pageKey: prefilledKey || "", title: prefilledTitle || "", embedUrl: "", youtubeUrl: "", status: "draft" });
    setVideoDialog({ open: true, editing: null });
  }

  function openEditVideo(v: ExplainerVideo) {
    setVideoForm({ pageKey: v.pageKey, title: v.title, embedUrl: v.embedUrl ?? "", youtubeUrl: v.youtubeUrl ?? "", status: (v.status as "draft" | "pending" | "final") || "draft" });
    setVideoDialog({ open: true, editing: v });
  }

  function handleVideoSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (videoDialog.editing) {
      updateVideoMutation.mutate({ id: videoDialog.editing.id, data: videoForm });
    } else {
      createVideoMutation.mutate(videoForm);
    }
  }

  const approvalMutation = useMutation({
    mutationFn: async ({ id, approved }: { id: number; approved: boolean }) => {
      await apiRequest("PATCH", `/api/admin/app-users/${id}/approval`, { approved });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/app-users"] });
      toast({ title: "User updated", description: "Approval status has been changed." });
    },
    onError: (err: unknown) => {
      toast({
        title: "Failed to update approval",
        description: parseErrorMessage(err, "Could not update approval status."),
        variant: "destructive",
      });
    },
  });

  // Task #1521 — Admin reset of an app user's TOTP enrolment when they
  // lose both their authenticator and backup codes. Mirrors the partner
  // and vendor team member resets added in Task #1514. The path contains
  // `/reset-totp` so the impersonation high-risk guard already covers it.
  const appUserResetTotpMutation = useMutation({
    meta: { silentSave: true },
    mutationFn: async (id: number) => apiRequest("POST", `/api/admin/app-users/${id}/reset-totp`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/app-users"] });
      toast({ title: "Two-factor reset", description: "User can re-enrol on next login." });
    },
    onError: (err: unknown) => {
      toast({
        title: "Failed to reset 2FA",
        description: parseErrorMessage(err, "Unknown error"),
        variant: "destructive",
      });
    },
  });

  // Task #4354: One-click re-engagement email for at-risk / dormant hosts,
  // fired directly from the churn-watch row so admins can act on the daily
  // churn-risk digest without hand-composing an email.
  const reengageMutation = useMutation({
    meta: { silentSave: true },
    mutationFn: async (id: number) => apiRequest("POST", `/api/admin/app-users/${id}/reengage`),
    onSuccess: async (res) => {
      let description = "Re-engagement email sent.";
      try {
        const body = await res.json();
        if (body?.message) description = body.message;
      } catch {
        // Non-JSON response — fall back to the generic message.
      }
      toast({ title: "Re-engagement email sent", description });
    },
    onError: (err: unknown) => {
      toast({
        title: "Failed to send re-engagement email",
        description: parseErrorMessage(err, "Unknown error"),
        variant: "destructive",
      });
    },
  });

  // Task #679: Suspend / Reinstate app user. Suspending forces sign-out
  // across every active session via storage.updateAppUser (Task #676).
  // Task #866 — admin override: assign or clear an app user's messaging
  // phone number directly (does not call Twilio).
  const [messagingPhoneDialog, setMessagingPhoneDialog] = useState<{
    open: boolean;
    user: AppUser | null;
    phone: string;
    sid: string;
  }>({ open: false, user: null, phone: "", sid: "" });
  const messagingPhoneMutation = useMutation({
    mutationFn: async (vars: { id: number; phone: string | null; sid: string | null }) => {
      await apiRequest("PATCH", `/api/admin/users/${vars.id}/messaging-phone`, {
        messagingPhoneNumber: vars.phone,
        messagingPhoneNumberSid: vars.sid,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/app-users"] });
      setMessagingPhoneDialog({ open: false, user: null, phone: "", sid: "" });
      toast({ title: "Messaging number updated" });
    },
    onError: (err: any) => {
      toast({
        title: "Failed to update messaging number",
        description: parseErrorMessage(err, "Unknown error"),
        variant: "destructive",
      });
    },
  });

  const [appUserSuspendDialog, setAppUserSuspendDialog] = useState<{
    open: boolean;
    user: AppUser | null;
    reason: string;
  }>({ open: false, user: null, reason: "" });
  const [appUserReinstateDialog, setAppUserReinstateDialog] = useState<{
    open: boolean;
    user: AppUser | null;
  }>({ open: false, user: null });

  // Task #3447: Per-row Impersonate action. Mirrors the dedicated
  // /app/dev-tools/impersonate flow — re-authenticates the super-admin with
  // their password, then starts an impersonation session and redirects into
  // the impersonated host's dashboard.
  const [impersonateDialog, setImpersonateDialog] = useState<{
    open: boolean;
    user: AppUserWithStatusEvent | null;
    password: string;
  }>({ open: false, user: null, password: "" });

  const impersonateMutation = useMutation({
    meta: { silentSave: true },
    mutationFn: async ({ targetUserId, password }: { targetUserId: number; password: string }) => {
      const res = await apiRequest("POST", "/api/app/impersonation/start", { targetUserId, password });
      return res.json();
    },
    onSuccess: () => {
      setImpersonateDialog({ open: false, user: null, password: "" });
      queryClient.clear();
      window.location.assign("/app/dashboard");
    },
    onError: (err: any) => {
      toast({
        title: "Failed",
        description: parseErrorMessage(err, "Could not start impersonation session."),
        variant: "destructive",
      });
    },
  });

  const appUserStatusMutation = useMutation({
    mutationFn: async ({
      id,
      status,
      reason,
    }: {
      id: number;
      status: "active" | "suspended";
      reason?: string | null;
    }) => {
      // Task #690: Forward the optional admin-supplied reason so the
      // suspension email can include context.
      // Task #689: The same reason is persisted as part of the audit trail.
      const payload: { status: typeof status; reason?: string } = { status };
      const trimmedReason = reason?.trim();
      if (trimmedReason) payload.reason = trimmedReason;
      await apiRequest("PATCH", `/api/admin/app-users/${id}/status`, payload);
    },
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/app-users"] });
      setAppUserSuspendDialog({ open: false, user: null, reason: "" });
      setAppUserReinstateDialog({ open: false, user: null });
      toast({
        title: vars.status === "suspended" ? "User suspended" : "User reinstated",
        // Task #690: Surface that a transactional notification email was
        // queued so admins know the user is being told what happened.
        description:
          vars.status === "suspended"
            ? "The user has been signed out of every device. A suspension notification email has been queued."
            : "The user can sign in again. A reinstatement notification email has been queued.",
      });
    },
    onError: (err: any) => {
      toast({
        title: "Error",
        description: parseErrorMessage(err, "Failed to update user status."),
        variant: "destructive",
      });
    },
  });

  // Task #1291: Admin override for subscription_status. Replaces ad-hoc
  // SQL UPDATE statements that used to be the only way to mark a user
  // 'comped' / 'active' when they were granted a paid plan outside Stripe.
  const [subStatusDialog, setSubStatusDialog] = useState<{
    open: boolean;
    user: AppUserWithStatusEvent | null;
    status: SubscriptionStatus;
    reason: string;
  }>({ open: false, user: null, status: "comped", reason: "" });

  // Task #1298: "Subscription history" modal — full audit trail of every
  // subscription_status change for the selected user (admin overrides,
  // founder grants, founder-intent activations). The latest event is
  // already surfaced as a tooltip; this drawer shows the complete history
  // compliance/support need for billing disputes.
  const [subHistoryDialog, setSubHistoryDialog] = useState<{
    open: boolean;
    user: AppUserWithStatusEvent | null;
  }>({ open: false, user: null });

  // Task #2835: Read-only Crisp support conversation history for a host,
  // surfaced in-app so support staff don't context-switch to Crisp.
  const [crispDialog, setCrispDialog] = useState<{
    open: boolean;
    user: AppUserWithStatusEvent | null;
  }>({ open: false, user: null });

  // Task #1523: Filter and sort the App Users table by 2FA enrolment status
  // so admins can audit adoption and quickly find users who can/can't have
  // their authenticator reset.
  const [appUsers2faFilter, setAppUsers2faFilter] = useState<"all" | "enabled" | "disabled">("all");
  const [appUsers2faSortDir, setAppUsers2faSortDir] = useState<"none" | "enabledFirst" | "disabledFirst">("none");

  // Task #3320: Filter and sort the App Users table by subscription status
  // (comped, canceled, etc.) and by the latest status-change time so support
  // and finance can quickly audit recent money-status overrides. Uses the
  // latestSubscriptionStatusEvent already returned per row.
  const [appUsersSubFilter, setAppUsersSubFilter] = useState<"all" | SubscriptionStatus | "none">("all");
  const [appUsersSubSortDir, setAppUsersSubSortDir] = useState<"none" | "newest" | "oldest">("none");
  // Task #3334: Narrow the App Users table to accounts whose latest subscription
  // status change happened within a recent window (7 / 30 / 90 days) so support
  // and finance can see only *recently* comped/canceled accounts. Rows with no
  // subscription status event are excluded when a window is selected.
  const [appUsersSubWindow, setAppUsersSubWindow] = useState<"all" | "7" | "30" | "90">("all");

  // Task #4329: Activity / churn-watch filters and sort.
  const [appUsersEngagementFilter, setAppUsersEngagementFilter] = useState<"all" | EngagementBucket>("all");
  const [appUsersActivitySortDir, setAppUsersActivitySortDir] = useState<"none" | "atRiskFirst" | "mostActive">("none");
  // Task #4348: filter to onboarding-outreach candidates by tracked sections —
  // "dashboard-only" (the only section ever touched is the dashboard) or users
  // who have *some* tracked activity but never touched a chosen key section.
  const [appUsersSectionFilter, setAppUsersSectionFilter] = useState<
    "all" | "dashboard-only" | "never-properties" | "never-rooms" | "never-financials"
  >("all");
  // Task #4334: churn-watch drill-down (30-day daily activity chart) target.
  const [activityDrilldown, setActivityDrilldown] = useState<{ userId: number; label: string } | null>(null);

  const subHistoryQuery = useQuery<AppUserSubscriptionStatusEvent[]>({
    queryKey: ["/api/admin/app-users", subHistoryDialog.user?.id, "subscription-status-events"],
    enabled: subHistoryDialog.open && subHistoryDialog.user != null,
  });

  // Task #4341: Per-feature usage breakdown for a single user, fetched lazily
  // when an admin opens the dialog from the Activity cell.
  const [sectionActivityDialog, setSectionActivityDialog] = useState<{
    open: boolean;
    user: AppUserWithStatusEvent | null;
  }>({ open: false, user: null });

  const sectionActivityQuery = useQuery<UserSectionActivity>({
    queryKey: ["/api/admin/app-users", sectionActivityDialog.user?.id, "section-activity"],
    enabled: sectionActivityDialog.open && sectionActivityDialog.user != null,
  });

  // Paid-status audit trail for a single founder intent, surfaced in a modal
  // from the Founder Signups tab so admins can review who marked it paid/unpaid
  // (and when) for this money-related action.
  const [founderPaidHistoryDialog, setFounderPaidHistoryDialog] = useState<{
    open: boolean;
    intent: FounderIntentWithPaidEvent | null;
  }>({ open: false, intent: null });

  const founderPaidHistoryQuery = useQuery<FounderIntentPaidEvent[]>({
    queryKey: ["/api/admin/founder-intents", founderPaidHistoryDialog.intent?.id, "paid-events"],
    enabled: founderPaidHistoryDialog.open && founderPaidHistoryDialog.intent != null,
  });

  const subscriptionStatusMutation = useMutation({
    mutationFn: async (vars: { id: number; subscriptionStatus: SubscriptionStatus; reason: string }) => {
      await apiRequest("PATCH", `/api/admin/app-users/${vars.id}/subscription-status`, {
        subscriptionStatus: vars.subscriptionStatus,
        reason: vars.reason,
      });
    },
    onSuccess: () => {
      // Task #1298: Invalidate broadly so both the user list (latest event
      // tooltip) and any open Subscription history modal refetch.
      queryClient.invalidateQueries({ queryKey: ["/api/admin/app-users"] });
      setSubStatusDialog({ open: false, user: null, status: "comped", reason: "" });
      toast({ title: "Subscription status updated" });
    },
    onError: (err: any) => {
      toast({
        title: "Failed to update subscription status",
        description: parseErrorMessage(err, "Unknown error"),
        variant: "destructive",
      });
    },
  });

  const logoutMutation = useMutation({
    meta: { silentSave: true },
    mutationFn: async () => {
      await apiRequest("POST", "/api/admin/logout", {});
    },
    onSuccess: () => {
      onLogout();
    },
  });

  const leads = leadsQuery.data || [];
  const featureReqs = featureRequestsQuery.data || [];
  const appUsersListAll = (appUsersQuery.data || []) as Array<AppUserWithStatusEvent & { activityStats: UserActivityStats | null }>;
  const appUsersList = (() => {
    let list = appUsersListAll;
    if (appUsers2faFilter === "enabled") list = list.filter((u) => u.totpEnabled);
    else if (appUsers2faFilter === "disabled") list = list.filter((u) => !u.totpEnabled);
    if (appUsersSubFilter !== "all") {
      list = list.filter((u) => (u.subscriptionStatus || "none") === appUsersSubFilter);
    }
    if (appUsersSubWindow !== "all") {
      const cutoff = Date.now() - Number(appUsersSubWindow) * 24 * 60 * 60 * 1000;
      list = list.filter((u) => {
        if (!u.latestSubscriptionStatusEvent) return false;
        const t = new Date(u.latestSubscriptionStatusEvent.createdAt as unknown as string).getTime();
        return Number.isFinite(t) && t >= cutoff;
      });
    }
    if (appUsersEngagementFilter !== "all") {
      list = list.filter((u) => {
        const bucket = u.activityStats?.engagementBucket ?? "Dormant";
        return bucket === appUsersEngagementFilter;
      });
    }
    if (appUsersSectionFilter !== "all") {
      list = list.filter((u) => {
        const touched = u.activityStats?.touchedSections ?? [];
        if (appUsersSectionFilter === "dashboard-only") {
          return touched.length === 1 && touched[0] === "dashboard";
        }
        // "never-X": users with *some* tracked activity who never touched X, so
        // the outreach list surfaces engaged-but-stuck hosts rather than every
        // fully-dormant account that never visited anything.
        const section = appUsersSectionFilter.slice("never-".length);
        return touched.length > 0 && !touched.includes(section);
      });
    }
    if (appUsers2faSortDir !== "none") {
      list = list.slice().sort((a, b) => {
        const av = a.totpEnabled ? 1 : 0;
        const bv = b.totpEnabled ? 1 : 0;
        return appUsers2faSortDir === "enabledFirst" ? bv - av : av - bv;
      });
    }
    if (appUsersSubSortDir !== "none") {
      list = list.slice().sort((a, b) => {
        const at = a.latestSubscriptionStatusEvent
          ? new Date(a.latestSubscriptionStatusEvent.createdAt as unknown as string).getTime()
          : 0;
        const bt = b.latestSubscriptionStatusEvent
          ? new Date(b.latestSubscriptionStatusEvent.createdAt as unknown as string).getTime()
          : 0;
        return appUsersSubSortDir === "newest" ? bt - at : at - bt;
      });
    }
    if (appUsersActivitySortDir !== "none") {
      // atRiskFirst: Dormant → At risk → Cooling off → Active (worst first)
      // mostActive: Active first, then by activeDays30 desc
      const BUCKET_ORDER: Record<EngagementBucket, number> = {
        "Dormant": 0, "At risk": 1, "Cooling off": 2, "Active": 3,
      };
      list = list.slice().sort((a, b) => {
        const ab = a.activityStats?.engagementBucket ?? "Dormant";
        const bb = b.activityStats?.engagementBucket ?? "Dormant";
        if (appUsersActivitySortDir === "atRiskFirst") {
          return BUCKET_ORDER[ab] - BUCKET_ORDER[bb];
        }
        // mostActive: sort by activeDays30 desc, then activeDays7 desc
        const ad30 = a.activityStats?.activeDays30 ?? 0;
        const bd30 = b.activityStats?.activeDays30 ?? 0;
        if (bd30 !== ad30) return bd30 - ad30;
        return (b.activityStats?.activeDays7 ?? 0) - (a.activityStats?.activeDays7 ?? 0);
      });
    }
    return list;
  })();
  const appUsers2faEnabledCount = appUsersListAll.filter((u) => u.totpEnabled).length;

  // focusUser deep link (/admin/users/:id → ?tab=app-users&focusUser=N —
  // Slack signup notifications + admin emails): once rows render, scroll the
  // requested user's row into view; the row itself gets a highlight ring.
  const focusUserId = new URLSearchParams(search).get("focusUser");
  useEffect(() => {
    if (!focusUserId || activeTab !== "app-users" || appUsersQuery.isLoading) return;
    const el = document.querySelector(`[data-testid="row-app-user-${focusUserId}"]`);
    if (el) el.scrollIntoView({ block: "center" });
  }, [focusUserId, activeTab, appUsersQuery.isLoading]);
  const founderIntentsAll = founderIntentsQuery.data || [];
  const founderTierCounts = {
    Founder25: founderIntentsAll.filter((i) => i.tier === "Founder25").length,
    Founder50: founderIntentsAll.filter((i) => i.tier === "Founder50").length,
    Founder100: founderIntentsAll.filter((i) => i.tier === "Founder100").length,
  };
  // Task #2039: anonymous tier-CTA click counts (top of funnel). Conversion
  // per tier = signups / clicks; rendered alongside the signup counts in the
  // Founder Signups tab so we can see modal drop-off at a glance.
  const founderTierClicks = founderTierClicksQuery.data || {
    Founder25: 0,
    Founder50: 0,
    Founder100: 0,
  } as Record<FounderTier, number>;
  function formatConversion(signups: number, clicks: number): string {
    if (!clicks) return "—";
    const pct = (signups / clicks) * 100;
    return `${pct.toFixed(pct >= 10 ? 0 : 1)}%`;
  }
  const founderIntents = founderIntentsAll
    .filter((i) => founderTierFilter === "all" || i.tier === founderTierFilter)
    .filter((i) =>
      founderPaidFilter === "all"
        ? true
        : founderPaidFilter === "paid"
          ? i.paidStatus === "paid"
          : i.paidStatus !== "paid",
    )
    .slice()
    .sort((a, b) => {
      const ta = new Date(a.createdAt as unknown as string).getTime();
      const tb = new Date(b.createdAt as unknown as string).getTime();
      return founderSortDir === "desc" ? tb - ta : ta - tb;
    });

  // Count context used by sidebar badges and (formerly) the dropdown nav
  const countCtx: AdminCountContext = {
    leads: leads.length,
    featureReqs: featureReqs.length,
    founderIntentsAll: founderIntentsAll.length,
    appUsersList: appUsersListAll.length,
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* ── Top nav bar ─────────────────────────────────────────────────── */}
      <nav className="sticky top-0 z-50 border-b bg-background/80 backdrop-blur-md">
        <div className="flex items-center justify-between gap-4 px-4 py-3 sm:px-6">
          <div className="flex items-center gap-3">
            {/* Mobile hamburger – only visible on small screens */}
            <button
              className="md:hidden p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-800 mr-1"
              onClick={() => setMobileSidebarOpen(true)}
              data-testid="button-admin-mobile-menu"
              aria-label="Open navigation"
            >
              <Menu className="h-5 w-5" />
            </button>
            <Link href="/">
              <Button variant="ghost" size="icon" data-testid="button-admin-back">
                <ArrowLeft className="h-4 w-4" />
              </Button>
            </Link>
            <div className="flex items-center gap-2">
              <Zap className="h-5 w-5 text-primary" />
              <span className="font-bold">PadPulse Admin</span>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {adminIdentity && (
              <span className="text-xs text-muted-foreground hidden sm:inline" data-testid="text-admin-identity">
                Signed in as {adminIdentity}
              </span>
            )}
            <GuideButton pageKey={`admin-${activeTab}`} compactOnMobile />
            <Button
              variant="ghost"
              size="sm"
              onClick={() => logoutMutation.mutate()}
              disabled={logoutMutation.isPending}
              data-testid="button-admin-logout"
            >
              <LogOut className="h-4 w-4 mr-1" />
              Sign Out
            </Button>
          </div>
        </div>
      </nav>

      {/* ── Body: sidebar + main content ───────────────────────────────── */}
      <div className="flex flex-1">
        {/* Mobile overlay backdrop */}
        {mobileSidebarOpen && (
          <div
            className="fixed inset-0 bg-black/40 z-30 md:hidden"
            onClick={() => setMobileSidebarOpen(false)}
            data-testid="admin-sidebar-backdrop"
          />
        )}

        {/* ── Left sidebar ─────────────────────────────────────────────── */}
        <aside
          className={`${
            mobileSidebarOpen ? "translate-x-0" : "-translate-x-full invisible md:visible md:translate-x-0"
          } fixed inset-y-0 left-0 z-40 w-56 border-r bg-white dark:bg-background flex flex-col transition-transform duration-300 md:static md:inset-auto md:z-auto`}
          data-testid="admin-sidebar"
        >
          <div className="flex-1 overflow-y-auto py-3">
            {/* Admin Pulse link */}
            <Link href="/admin/pulse">
              <span
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-brand hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer"
                data-testid="link-admin-pulse-sidebar"
              >
                <Gauge className="h-4 w-4" />
                Admin Pulse
              </span>
            </Link>

            {/* Collapse / Expand all */}
            <div className="flex items-center justify-end gap-3 px-4 pb-1">
              {!allCollapsed && (
                <button
                  onClick={collapseAll}
                  className="flex items-center gap-1 text-[10px] text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                  data-testid="admin-sidebar-collapse-all"
                >
                  <ChevronsUp className="h-3 w-3" />
                  Collapse all
                </button>
              )}
              {!allExpanded && (
                <button
                  onClick={expandAll}
                  className="flex items-center gap-1 text-[10px] text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                  data-testid="admin-sidebar-expand-all"
                >
                  <ChevronsDown className="h-3 w-3" />
                  Expand all
                </button>
              )}
            </div>

            {/* Nav sections (filtered to this admin's capabilities) */}
            {visibleNavSections.map((section) => {
              const isExpanded = expandedSections.has(section.label);
              const SectionIcon = section.icon;
              const isSectionActive = section.items.some(
                (item) => !item.href && item.value === activeTab,
              );
              const sectionSlug = section.label.toLowerCase().replace(/[\s&]+/g, "-");
              return (
                <div key={section.label}>
                  <button
                    onClick={() => toggleSection(section.label)}
                    className={`w-full flex items-center gap-2 px-4 py-1.5 mt-0.5 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800`}
                    data-testid={`admin-section-${sectionSlug}`}
                    aria-expanded={isExpanded}
                  >
                    <SectionIcon
                      className={`h-3.5 w-3.5 ${isSectionActive ? "text-brand" : "text-gray-400"}`}
                    />
                    <span
                      className={`flex-1 text-left text-[11px] font-semibold uppercase tracking-wider ${
                        isSectionActive ? "text-brand" : "text-gray-400"
                      }`}
                    >
                      {section.label}
                    </span>
                    {isExpanded ? (
                      <ChevronDown className="h-3 w-3 text-gray-400" />
                    ) : (
                      <ChevronRight className="h-3 w-3 text-gray-400" />
                    )}
                  </button>
                  {isExpanded &&
                    section.items.map((item) => {
                      const ItemIcon = item.icon;
                      const isActive = !item.href && item.value === activeTab;
                      const count = item.getCount ? item.getCount(countCtx) : null;
                      return (
                        <button
                          key={item.value}
                          onClick={() => {
                            if (item.href) {
                              window.location.href = item.href;
                            } else {
                              setActiveTab(item.value);
                              setMobileSidebarOpen(false);
                            }
                          }}
                          className={`w-full flex items-center gap-3 pl-8 pr-2 py-2 text-sm cursor-pointer ${
                            isActive
                              ? "text-gray-900 dark:text-gray-100 font-medium bg-brand-muted dark:bg-gray-800"
                              : "text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800"
                          }`}
                          data-testid={`admin-item-${item.value}`}
                        >
                          <ItemIcon className="h-4 w-4 shrink-0" />
                          <span className="flex-1 text-left">{item.label}</span>
                          {count !== null && count > 0 && (
                            <span className="ml-auto text-xs tabular-nums text-muted-foreground">
                              ({count})
                            </span>
                          )}
                          {item.href && (
                            <ExternalLink className="h-3 w-3 ml-auto shrink-0 text-muted-foreground" />
                          )}
                        </button>
                      );
                    })}
                </div>
              );
            })}
          </div>
        </aside>

        {/* ── Main content ─────────────────────────────────────────────── */}
        <main className="flex-1 min-w-0 overflow-auto" data-testid="admin-main-content">
          <div className="px-4 py-8 sm:px-6">
        <GuideNudgeBanner pageKey={`admin-${activeTab}`} />
        <AuditLogHealthBanner onView={() => setActiveTab("audit-log-health")} />
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3 lg:grid-cols-6 mb-8">
          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-md bg-primary/10">
                <Users className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold" data-testid="text-total-leads">
                  {leadsQuery.isLoading ? <Skeleton className="h-7 w-12" /> : leads.length}
                </p>
                <p className="text-sm text-muted-foreground">Total Leads</p>
              </div>
            </div>
          </Card>
          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-md bg-primary/10">
                <UserCheck className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold" data-testid="text-total-app-users">
                  {appUsersQuery.isLoading ? <Skeleton className="h-7 w-12" /> : appUsersListAll.length}
                </p>
                <p className="text-sm text-muted-foreground">App Users</p>
              </div>
            </div>
          </Card>
          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-md bg-primary/10">
                <Lightbulb className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold" data-testid="text-total-features">
                  {featureRequestsQuery.isLoading ? <Skeleton className="h-7 w-12" /> : featureReqs.length}
                </p>
                <p className="text-sm text-muted-foreground">Feature Requests</p>
              </div>
            </div>
          </Card>
          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-md bg-primary/10">
                <Crown className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold" data-testid="text-total-founder-intents">
                  {founderIntentsQuery.isLoading ? <Skeleton className="h-7 w-12" /> : founderIntentsAll.length}
                </p>
                <p className="text-sm text-muted-foreground">Founder Signups</p>
              </div>
            </div>
          </Card>
          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-md bg-primary/10">
                <Download className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground mb-1">Export Data</p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => downloadCSV(leads)}
                  disabled={leads.length === 0}
                  data-testid="button-export-csv"
                  data-tour="admin-leads-export"
                >
                  Download CSV
                </Button>
              </div>
            </div>
          </Card>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsContent value="leads" data-tour="admin-leads-section">
            <Card>
              <div className="flex items-center justify-between px-4 py-3 border-b">
                <p className="text-sm text-muted-foreground font-medium">
                  {showArchivedLeads ? "Archived leads" : "Active leads"}
                </p>
                <div className="flex items-center gap-2">
                  <Label htmlFor="toggle-archived-leads" className="text-sm text-muted-foreground cursor-pointer">
                    Show archived
                  </Label>
                  <Switch
                    id="toggle-archived-leads"
                    data-testid="toggle-archived-leads"
                    checked={showArchivedLeads}
                    onCheckedChange={setShowArchivedLeads}
                  />
                </div>
              </div>
              {leadsQuery.isLoading ? (
                <div className="p-6 space-y-3">
                  {[1, 2, 3].map((i) => (
                    <Skeleton key={i} className="h-12 w-full" />
                  ))}
                </div>
              ) : leads.length === 0 ? (
                <div className="p-12 text-center">
                  <Users className="mx-auto h-10 w-10 text-muted-foreground/40 mb-3" />
                  <p className="text-muted-foreground">
                    {showArchivedLeads ? "No archived leads." : "No leads yet."}
                  </p>
                  <p className="text-sm text-muted-foreground/70">
                    {showArchivedLeads
                      ? "Archived leads will appear here."
                      : "When visitors sign up on the landing page, they'll appear here."}
                  </p>
                </div>
              ) : (
                <div className="overflow-x-auto" data-tour="admin-leads-table">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Name</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead>Company</TableHead>
                        <TableHead>Co-Living Stage</TableHead>
                        <TableHead>Plan</TableHead>
                        <TableHead>How they heard</TableHead>
                        <TableHead>Mgmt Interest</TableHead>
                        <TableHead>Date</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {leads.map((lead) => {
                        const invitedAt = lead.invitedAt ? new Date(lead.invitedAt as unknown as string) : null;
                        const withinCooldown = invitedAt != null && (Date.now() - invitedAt.getTime()) < 24 * 60 * 60 * 1000;
                        const isArchived = !!lead.archivedAt;
                        return (
                        <TableRow key={lead.id} data-testid={`row-lead-${lead.id}`} className={isArchived ? "opacity-60" : ""}>
                          <TableCell className="font-medium">
                            {lead.fullName || "—"}
                          </TableCell>
                          <TableCell>{lead.email}</TableCell>
                          <TableCell className="text-muted-foreground">
                            {lead.company || "—"}
                          </TableCell>
                          <TableCell>
                            {lead.journeyStage ? formatStage(lead.journeyStage) : "—"}
                          </TableCell>
                          <TableCell>
                            <Badge variant={planVariant(lead.selectedPlan)}>
                              {planLabel(lead.selectedPlan)}
                            </Badge>
                          </TableCell>
                          <TableCell
                            className="text-muted-foreground text-sm max-w-xs truncate"
                            title={lead.howDidYouHearAboutUs || ""}
                            data-testid={`text-lead-source-${lead.id}`}
                          >
                            {lead.howDidYouHearAboutUs || "—"}
                          </TableCell>
                          <TableCell data-testid={`text-lead-mgmt-interest-${lead.id}`}>
                            {lead.interestedInManagement === true ? (
                              <Badge variant="default" className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">Yes</Badge>
                            ) : lead.interestedInManagement === false ? (
                              <Badge variant="secondary">No</Badge>
                            ) : (
                              <span className="text-muted-foreground text-sm">—</span>
                            )}
                          </TableCell>
                          <TableCell className="text-muted-foreground text-sm">
                            {formatDate(lead.createdAt as unknown as string)}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1">
                              {!isArchived && (
                                <LeadInviteButton lead={lead} withinCooldown={withinCooldown} invitedAt={invitedAt} />
                              )}
                              <LeadArchiveButton
                                lead={lead}
                                onDone={() => queryClient.invalidateQueries({ queryKey: ["/api/leads", showArchivedLeads ? "archived" : "active"] })}
                              />
                            </div>
                          </TableCell>
                        </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              )}
            </Card>
          </TabsContent>

          <TabsContent value="features" data-testid="panel-features" data-tour="admin-features-section">
            <Card>
              {featureRequestsQuery.isLoading ? (
                <div className="p-6 space-y-3">
                  {[1, 2, 3].map((i) => (
                    <Skeleton key={i} className="h-12 w-full" />
                  ))}
                </div>
              ) : featureReqs.length === 0 ? (
                <div className="p-12 text-center">
                  <Lightbulb className="mx-auto h-10 w-10 text-muted-foreground/40 mb-3" />
                  <p className="text-muted-foreground">
                    No feature requests yet.
                  </p>
                  <p className="text-sm text-muted-foreground/70">
                    Feature requests from the landing page will appear here.
                  </p>
                </div>
              ) : (
                <div className="overflow-x-auto" data-tour="admin-features-table">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Name</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead className="w-[40%]">Description</TableHead>
                        <TableHead>Date</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {featureReqs.map((req) => (
                        <TableRow
                          key={req.id}
                          data-testid={`row-feature-${req.id}`}
                        >
                          <TableCell className="font-medium">
                            {req.name}
                          </TableCell>
                          <TableCell>{req.email}</TableCell>
                          <TableCell className="text-sm">
                            {req.description}
                          </TableCell>
                          <TableCell className="text-muted-foreground text-sm">
                            {formatDate(req.createdAt as unknown as string)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </Card>
          </TabsContent>

          <TabsContent value="app-users" data-tour="admin-app-users-section">
            <Card>
              {appUsersQuery.isLoading ? (
                <div className="p-6 space-y-3">
                  {[1, 2, 3].map((i) => (
                    <Skeleton key={i} className="h-12 w-full" />
                  ))}
                </div>
              ) : appUsersListAll.length === 0 ? (
                <div className="p-12 text-center">
                  <UserCheck className="mx-auto h-10 w-10 text-muted-foreground/40 mb-3" />
                  <p className="text-muted-foreground">No app users yet.</p>
                  <p className="text-sm text-muted-foreground/70">
                    Sign-ups are approved automatically and appear here as soon as they verify their email.
                  </p>
                </div>
              ) : (
                <>
                <div className="flex flex-wrap items-center gap-3 p-4 border-b" data-testid="toolbar-app-users" data-tour="admin-app-users-filters">
                  <div className="flex items-center gap-2">
                    <Label className="text-sm">2FA:</Label>
                    <Select
                      value={appUsers2faFilter}
                      onValueChange={(v) => setAppUsers2faFilter(v as "all" | "enabled" | "disabled")}
                    >
                      <SelectTrigger className="w-44 h-9" data-testid="select-app-users-2fa-filter">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All users</SelectItem>
                        <SelectItem value="enabled">2FA enabled</SelectItem>
                        <SelectItem value="disabled">2FA disabled</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-center gap-2">
                    <Label className="text-sm">Subscription:</Label>
                    <Select
                      value={appUsersSubFilter}
                      onValueChange={(v) => setAppUsersSubFilter(v as "all" | SubscriptionStatus | "none")}
                    >
                      <SelectTrigger className="w-44 h-9" data-testid="select-app-users-sub-filter">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All statuses</SelectItem>
                        <SelectItem value="comped">Comped</SelectItem>
                        <SelectItem value="canceled">Canceled</SelectItem>
                        <SelectItem value="active">Active</SelectItem>
                        <SelectItem value="trialing">Trialing</SelectItem>
                        <SelectItem value="past_due">Past due</SelectItem>
                        <SelectItem value="none">None</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-center gap-2">
                    <Label className="text-sm">Changed within:</Label>
                    <Select
                      value={appUsersSubWindow}
                      onValueChange={(v) => setAppUsersSubWindow(v as "all" | "7" | "30" | "90")}
                    >
                      <SelectTrigger className="w-44 h-9" data-testid="select-app-users-sub-window">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Any time</SelectItem>
                        <SelectItem value="7">Last 7 days</SelectItem>
                        <SelectItem value="30">Last 30 days</SelectItem>
                        <SelectItem value="90">Last 90 days</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-center gap-2">
                    <Label className="text-sm">Engagement:</Label>
                    <Select
                      value={appUsersEngagementFilter}
                      onValueChange={(v) => setAppUsersEngagementFilter(v as "all" | EngagementBucket)}
                    >
                      <SelectTrigger className="w-44 h-9" data-testid="select-app-users-engagement-filter">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All engagement</SelectItem>
                        <SelectItem value="Active">Active</SelectItem>
                        <SelectItem value="Cooling off">Cooling off</SelectItem>
                        <SelectItem value="At risk">At risk</SelectItem>
                        <SelectItem value="Dormant">Dormant</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-center gap-2">
                    <Label className="text-sm">Sections:</Label>
                    <Select
                      value={appUsersSectionFilter}
                      onValueChange={(v) =>
                        setAppUsersSectionFilter(
                          v as
                            | "all"
                            | "dashboard-only"
                            | "never-properties"
                            | "never-rooms"
                            | "never-financials",
                        )
                      }
                    >
                      <SelectTrigger className="w-52 h-9" data-testid="select-app-users-section-filter">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All sections</SelectItem>
                        <SelectItem value="dashboard-only">Dashboard-only</SelectItem>
                        <SelectItem value="never-properties">Never visited Properties</SelectItem>
                        <SelectItem value="never-rooms">Never visited Rooms</SelectItem>
                        <SelectItem value="never-financials">Never visited Financials</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="text-xs text-muted-foreground" data-testid="text-app-users-2fa-summary">
                    {appUsers2faEnabledCount} of {appUsersListAll.length} users have 2FA enabled
                    {appUsers2faFilter !== "all" ||
                    appUsers2faSortDir !== "none" ||
                    appUsersSubFilter !== "all" ||
                    appUsersSubWindow !== "all" ||
                    appUsersSubSortDir !== "none" ||
                    appUsersEngagementFilter !== "all" ||
                    appUsersSectionFilter !== "all" ||
                    appUsersActivitySortDir !== "none"
                      ? ` · showing ${appUsersList.length}`
                      : ""}
                  </div>
                </div>
                <div className="overflow-x-auto" data-tour="admin-app-users-table">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Name</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead>Company</TableHead>
                        <TableHead className="text-center">Houses</TableHead>
                        <TableHead className="text-center">Rooms</TableHead>
                        <TableHead>Req. Plan</TableHead>
                        <TableHead>Req. Billing</TableHead>
                        <TableHead>Approval</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>
                          <button
                            type="button"
                            className="inline-flex items-center gap-1 hover:text-foreground"
                            onClick={() =>
                              setAppUsersSubSortDir((d) =>
                                d === "none" ? "newest" : d === "newest" ? "oldest" : "none",
                              )
                            }
                            data-testid="button-sort-app-users-sub"
                            title="Sort by latest subscription status change"
                          >
                            Subscription
                            {appUsersSubSortDir === "newest" ? (
                              <ArrowDown className="h-3 w-3" />
                            ) : appUsersSubSortDir === "oldest" ? (
                              <ArrowUp className="h-3 w-3" />
                            ) : (
                              <ArrowUpDown className="h-3 w-3 opacity-50" />
                            )}
                          </button>
                        </TableHead>
                        <TableHead>
                          <button
                            type="button"
                            className="inline-flex items-center gap-1 hover:text-foreground"
                            onClick={() =>
                              setAppUsers2faSortDir((d) =>
                                d === "none" ? "enabledFirst" : d === "enabledFirst" ? "disabledFirst" : "none",
                              )
                            }
                            data-testid="button-sort-app-users-2fa"
                            title="Sort by 2FA status"
                          >
                            2FA
                            {appUsers2faSortDir === "enabledFirst" ? (
                              <ArrowDown className="h-3 w-3" />
                            ) : appUsers2faSortDir === "disabledFirst" ? (
                              <ArrowUp className="h-3 w-3" />
                            ) : (
                              <ArrowUpDown className="h-3 w-3 opacity-50" />
                            )}
                          </button>
                        </TableHead>
                        <TableHead>Msg #</TableHead>
                        <TableHead>Date</TableHead>
                        <TableHead>
                          <button
                            type="button"
                            className="inline-flex items-center gap-1 hover:text-foreground"
                            onClick={() =>
                              setAppUsersActivitySortDir((d) =>
                                d === "none" ? "atRiskFirst" : d === "atRiskFirst" ? "mostActive" : "none",
                              )
                            }
                            data-testid="button-sort-app-users-activity"
                            title="Sort by engagement (at-risk first or most-active first)"
                          >
                            Last Active
                            {appUsersActivitySortDir === "atRiskFirst" ? (
                              <ChevronsDown className="h-3 w-3" />
                            ) : appUsersActivitySortDir === "mostActive" ? (
                              <ChevronsUp className="h-3 w-3" />
                            ) : (
                              <ArrowUpDown className="h-3 w-3 opacity-50" />
                            )}
                          </button>
                        </TableHead>
                        <TableHead className="text-center">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {appUsersList.map((appUser) => {
                        const isSuspended = appUser.status === "suspended";
                        return (
                          <TableRow
                            key={appUser.id}
                            data-testid={`row-app-user-${appUser.id}`}
                            className={focusUserId === String(appUser.id) ? "bg-brand/10" : undefined}
                          >
                            <TableCell className="font-medium">
                              {appUser.fullName || appUser.name || "—"}
                            </TableCell>
                            <TableCell>{appUser.email}</TableCell>
                            <TableCell className="text-muted-foreground">
                              {appUser.company || "—"}
                            </TableCell>
                            <TableCell className="text-center">
                              {appUser.numberOfHouses || "—"}
                            </TableCell>
                            <TableCell className="text-center">
                              {appUser.totalRooms || "—"}
                            </TableCell>
                            <TableCell className="text-sm text-muted-foreground" data-testid={`text-req-plan-${appUser.id}`}>
                              {(appUser as any).requestedPlan || "—"}
                            </TableCell>
                            <TableCell className="text-sm text-muted-foreground" data-testid={`text-req-billing-${appUser.id}`}>
                              {(appUser as any).requestedBillingInterval || "—"}
                            </TableCell>
                            <TableCell>
                              {/* Self-serve signup auto-approves every new account;
                                  an unapproved row is a legacy leftover or an
                                  admin-revoked account, never a pending signup. */}
                              <Badge variant={appUser.approved ? "default" : "destructive"}>
                                {appUser.approved ? "Approved" : "Blocked (legacy)"}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              {isSuspended && appUser.latestStatusEvent ? (
                                <TooltipProvider delayDuration={150}>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <Badge
                                        variant="destructive"
                                        className="cursor-help"
                                        data-testid={`badge-app-user-status-${appUser.id}`}
                                      >
                                        Suspended
                                      </Badge>
                                    </TooltipTrigger>
                                    <TooltipContent
                                      side="top"
                                      className="max-w-xs space-y-1 text-xs"
                                      data-testid={`tooltip-suspension-${appUser.id}`}
                                    >
                                      <div className="font-semibold">
                                        Suspended {formatDate(appUser.latestStatusEvent.createdAt as unknown as string)}
                                      </div>
                                      <div data-testid={`text-suspension-reason-${appUser.id}`}>
                                        <span className="text-muted-foreground">Reason:</span>{" "}
                                        {appUser.latestStatusEvent.reason || "—"}
                                      </div>
                                      <div data-testid={`text-suspension-actor-${appUser.id}`}>
                                        <span className="text-muted-foreground">By:</span>{" "}
                                        {appUser.latestStatusEvent.adminLabel || "Unknown admin"}
                                      </div>
                                    </TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                              ) : (
                                <Badge
                                  variant={isSuspended ? "destructive" : "outline"}
                                  data-testid={`badge-app-user-status-${appUser.id}`}
                                >
                                  {/* Self-serve signup: 'waitlist' can no longer be
                                      entered (prod was verified drained Aug 2026);
                                      label any stray row as legacy so admins don't
                                      think an approval queue still exists. */}
                                  {isSuspended
                                    ? "Suspended"
                                    : appUser.status === "waitlist"
                                      ? "waitlist (legacy)"
                                      : (appUser.status || "active")}
                                </Badge>
                              )}
                            </TableCell>
                            <TableCell>
                              {(() => {
                                // Task #1291: Subscription status badge with optional
                                // tooltip showing the latest admin override (who/why).
                                const sub = (appUser.subscriptionStatus || "none") as SubscriptionStatus;
                                const variant =
                                  sub === "active" || sub === "trialing" || sub === "comped"
                                    ? "default"
                                    : sub === "past_due"
                                      ? "destructive"
                                      : sub === "canceled"
                                        ? "destructive"
                                        : "secondary";
                                const evt = appUser.latestSubscriptionStatusEvent;
                                const badge = (
                                  <Badge
                                    variant={variant}
                                    className={evt ? "cursor-help" : undefined}
                                    data-testid={`badge-app-user-sub-status-${appUser.id}`}
                                  >
                                    {sub}
                                  </Badge>
                                );
                                if (!evt) return badge;
                                // Task #3276: surface the acting admin + timestamp
                                // inline (not just on hover) for the latest status
                                // change, mirroring the Founder Signups paid-toggle
                                // attribution. The tooltip still carries the reason.
                                const actorLabel = evt.adminLabel
                                  ? `${subStatusSourceLabel(evt.source)} · ${evt.adminLabel}`
                                  : subStatusSourceLabel(evt.source);
                                return (
                                  <div className="flex flex-col gap-0.5">
                                    <TooltipProvider delayDuration={150}>
                                      <Tooltip>
                                        <TooltipTrigger asChild>{badge}</TooltipTrigger>
                                        <TooltipContent
                                          side="top"
                                          className="max-w-xs space-y-1 text-xs"
                                          data-testid={`tooltip-sub-status-${appUser.id}`}
                                        >
                                          <div className="font-semibold">
                                            {evt.previousStatus} → {evt.newStatus} (
                                            {formatDate(evt.createdAt as unknown as string)})
                                          </div>
                                          <div data-testid={`text-sub-status-reason-${appUser.id}`}>
                                            <span className="text-muted-foreground">Reason:</span> {evt.reason}
                                          </div>
                                          <div data-testid={`text-sub-status-actor-${appUser.id}`}>
                                            <span className="text-muted-foreground">By:</span>{" "}
                                            {evt.adminLabel || "Unknown admin"}
                                          </div>
                                          <div className="text-muted-foreground">
                                            Source: {evt.source}
                                          </div>
                                        </TooltipContent>
                                      </Tooltip>
                                    </TooltipProvider>
                                    <Badge
                                      variant="outline"
                                      className="w-fit text-[10px] px-1.5 py-0 h-4 font-normal text-muted-foreground"
                                      data-testid={`badge-sub-status-actor-${appUser.id}`}
                                    >
                                      {actorLabel}
                                    </Badge>
                                    <span
                                      className="text-[10px] text-muted-foreground whitespace-nowrap"
                                      data-testid={`text-sub-status-at-${appUser.id}`}
                                    >
                                      {formatDate(evt.createdAt as unknown as string)}
                                    </span>
                                  </div>
                                );
                              })()}
                            </TableCell>
                            <TableCell data-testid={`cell-app-user-2fa-${appUser.id}`}>
                              {appUser.totpEnabled ? (
                                <TooltipProvider delayDuration={150}>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <Badge
                                        variant="default"
                                        className="cursor-help gap-1"
                                        data-testid={`badge-app-user-2fa-${appUser.id}`}
                                      >
                                        <Shield className="h-3 w-3" />
                                        Enabled
                                      </Badge>
                                    </TooltipTrigger>
                                    <TooltipContent
                                      side="top"
                                      className="text-xs"
                                      data-testid={`tooltip-app-user-2fa-${appUser.id}`}
                                    >
                                      {appUser.totpEnrolledAt
                                        ? `Enrolled ${formatDate(appUser.totpEnrolledAt as unknown as string)}`
                                        : "Authenticator-app 2FA is enabled"}
                                    </TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                              ) : (
                                <Badge
                                  variant="outline"
                                  className="text-muted-foreground"
                                  data-testid={`badge-app-user-2fa-${appUser.id}`}
                                >
                                  Disabled
                                </Badge>
                              )}
                            </TableCell>
                            <TableCell className="text-muted-foreground text-xs whitespace-nowrap" data-testid={`text-msg-phone-${appUser.id}`}>
                              <div className="flex items-center gap-2">
                                <span>{appUser.messagingPhoneNumber || "—"}</span>
                                {appUser.messagingPhoneNumberSid?.startsWith("PNE2E") && (
                                  <Badge
                                    variant="outline"
                                    className="text-[10px] px-1.5 py-0"
                                    data-testid={`badge-msg-phone-test-${appUser.id}`}
                                  >
                                    Test number
                                  </Badge>
                                )}
                              </div>
                            </TableCell>
                            <TableCell className="text-muted-foreground text-sm">
                              {formatDate(appUser.createdAt as unknown as string)}
                            </TableCell>
                            <TableCell data-testid={`cell-app-user-activity-${appUser.id}`}>
                              {(() => {
                                const openDrilldown = () =>
                                  setActivityDrilldown({
                                    userId: appUser.id,
                                    label: appUser.fullName || appUser.name || appUser.email || `User #${appUser.id}`,
                                  });
                                const stats = appUser.activityStats;
                                if (!stats || !stats.lastActive) {
                                  return (
                                    <div className="flex flex-col gap-1 items-start">
                                      <button
                                        type="button"
                                        onClick={openDrilldown}
                                        className="flex flex-col gap-1 items-start rounded hover:bg-muted/60 -mx-1 px-1 py-0.5 cursor-pointer"
                                        title="View 30-day activity"
                                        data-testid={`button-activity-drilldown-${appUser.id}`}
                                      >
                                        <span className="text-xs text-muted-foreground">—</span>
                                        <Badge variant="secondary" className="w-fit text-xs px-1.5 py-0 h-5 font-normal text-muted-foreground">
                                          Dormant
                                        </Badge>
                                      </button>
                                      <button
                                        type="button"
                                        onClick={() => setSectionActivityDialog({ open: true, user: appUser })}
                                        className="inline-flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground hover:underline"
                                        data-testid={`button-section-activity-${appUser.id}`}
                                        title="View per-feature usage breakdown"
                                      >
                                        <Gauge className="h-2.5 w-2.5 shrink-0" />
                                        Sections
                                      </button>
                                    </div>
                                  );
                                }
                                const bucketVariant: Record<string, string> = {
                                  "Active": "bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-200",
                                  "Cooling off": "bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200",
                                  "At risk": "bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200",
                                  "Dormant": "bg-muted text-muted-foreground",
                                  "New": "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200",
                                };
                                const cls = bucketVariant[stats.engagementBucket] ?? "bg-muted text-muted-foreground";
                                return (
                                  <div className="flex flex-col gap-1 items-start">
                                    <TooltipProvider delayDuration={150}>
                                      <Tooltip>
                                        <TooltipTrigger asChild>
                                          <button
                                            type="button"
                                            onClick={openDrilldown}
                                            className="flex flex-col gap-1 items-start text-left cursor-pointer rounded hover:bg-muted/60 -mx-1 px-1 py-0.5"
                                            title="View 30-day activity"
                                            data-testid={`button-activity-drilldown-${appUser.id}`}
                                          >
                                            <div className="flex items-center gap-1 text-xs text-muted-foreground whitespace-nowrap">
                                              <Clock className="h-3 w-3 shrink-0" />
                                              {stats.lastActive}
                                              <BarChart3 className="h-3 w-3 shrink-0 opacity-50" />
                                            </div>
                                            <div className={`inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[11px] font-medium w-fit ${cls}`} data-testid={`badge-engagement-${appUser.id}`}>
                                              <Activity className="h-2.5 w-2.5 shrink-0" />
                                              {stats.engagementBucket}
                                            </div>
                                          </button>
                                        </TooltipTrigger>
                                        <TooltipContent side="top" className="text-xs space-y-1 max-w-xs" data-testid={`tooltip-activity-${appUser.id}`}>
                                          <div className="font-semibold">Activity (last 7 / 30 days)</div>
                                          <div>Days active: <span className="font-medium">{stats.activeDays7}</span> / <span className="font-medium">{stats.activeDays30}</span></div>
                                          <div>Avg hits/day: <span className="font-medium">{stats.avgPageHitsPerDay7}</span> / <span className="font-medium">{stats.avgPageHitsPerDay30}</span></div>
                                          {stats.daysSinceActive !== null && (
                                            <div>Last active: <span className="font-medium">{stats.daysSinceActive === 0 ? "today" : `${stats.daysSinceActive}d ago`}</span></div>
                                          )}
                                        </TooltipContent>
                                      </Tooltip>
                                    </TooltipProvider>
                                    <button
                                      type="button"
                                      onClick={() => setSectionActivityDialog({ open: true, user: appUser })}
                                      className="inline-flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground hover:underline"
                                      data-testid={`button-section-activity-${appUser.id}`}
                                      title="View per-feature usage breakdown"
                                    >
                                      <Gauge className="h-2.5 w-2.5 shrink-0" />
                                      Sections
                                    </button>
                                  </div>
                                );
                              })()}
                            </TableCell>
                            <TableCell className="text-center">
                              <div className="flex items-center justify-center gap-2">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() =>
                                    setMessagingPhoneDialog({
                                      open: true,
                                      user: appUser,
                                      phone: appUser.messagingPhoneNumber || "",
                                      sid: appUser.messagingPhoneNumberSid || "",
                                    })
                                  }
                                  data-testid={`button-edit-msg-phone-${appUser.id}`}
                                >
                                  Msg #
                                </Button>
                                <Button
                                  variant={appUser.approved ? "outline" : "default"}
                                  size="sm"
                                  onClick={() => approvalMutation.mutate({ id: appUser.id, approved: !appUser.approved })}
                                  disabled={approvalMutation.isPending}
                                  data-testid={`button-toggle-approval-${appUser.id}`}
                                  data-tour="admin-app-users-approve"
                                >
                                  {appUser.approved ? "Revoke approval" : "Approve"}
                                </Button>
                                {isSuspended ? (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setAppUserReinstateDialog({ open: true, user: appUser })}
                                    disabled={appUserStatusMutation.isPending}
                                    data-testid={`button-reinstate-app-user-${appUser.id}`}
                                  >
                                    <UserCheck className="h-3.5 w-3.5 mr-1" /> Reinstate
                                  </Button>
                                ) : (
                                  <Button
                                    variant="destructive"
                                    size="sm"
                                    onClick={() => setAppUserSuspendDialog({ open: true, user: appUser, reason: "" })}
                                    disabled={appUserStatusMutation.isPending}
                                    data-testid={`button-suspend-app-user-${appUser.id}`}
                                    data-tour="admin-app-users-suspend"
                                  >
                                    <UserX className="h-3.5 w-3.5 mr-1" /> Suspend
                                  </Button>
                                )}
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() =>
                                    setSubStatusDialog({
                                      open: true,
                                      user: appUser,
                                      status: (appUser.subscriptionStatus as SubscriptionStatus) || "comped",
                                      reason: "",
                                    })
                                  }
                                  disabled={subscriptionStatusMutation.isPending}
                                  data-testid={`button-edit-sub-status-${appUser.id}`}
                                  data-tour="admin-app-users-sub-status"
                                >
                                  Sub Status
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() =>
                                    setImpersonateDialog({ open: true, user: appUser, password: "" })
                                  }
                                  disabled={impersonateMutation.isPending}
                                  data-testid={`button-impersonate-${appUser.id}`}
                                  data-tour="admin-app-users-impersonate"
                                  title="Start an impersonation session for this user"
                                >
                                  <UserCog className="h-3.5 w-3.5 mr-1" />
                                  Impersonate
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() =>
                                    setSubHistoryDialog({ open: true, user: appUser })
                                  }
                                  data-testid={`button-sub-status-history-${appUser.id}`}
                                  title="View subscription status history"
                                >
                                  <History className="h-3.5 w-3.5 mr-1" />
                                  History
                                </Button>
                                {(() => {
                                  // Task #4354: one-click re-engagement outreach.
                                  // Only offered for hosts who have actually
                                  // cooled off — an "Active" user doesn't need a
                                  // "we miss you" nudge. Demo accounts never egress.
                                  const bucket = appUser.activityStats?.engagementBucket ?? "Dormant";
                                  const showReengage = !appUser.isDemo && bucket !== "Active";
                                  if (!showReengage) return null;
                                  return (
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={() => {
                                        const label = appUser.fullName || appUser.name || appUser.email;
                                        if (confirm(`Send a re-engagement email to ${label} (${appUser.email})?`)) {
                                          reengageMutation.mutate(appUser.id);
                                        }
                                      }}
                                      disabled={reengageMutation.isPending}
                                      data-testid={`button-reengage-${appUser.id}`}
                                      title="Send a re-engagement email to this at-risk user"
                                    >
                                      {reengageMutation.isPending && reengageMutation.variables === appUser.id
                                        ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                                        : <Mail className="h-3.5 w-3.5 mr-1" />}
                                      Re-engage
                                    </Button>
                                  );
                                })()}
                                {(() => {
                                  const unread =
                                    crispUnreadCounts[(appUser.email || "").trim().toLowerCase()] ?? 0;
                                  return (
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      className="relative"
                                      onClick={() => setCrispDialog({ open: true, user: appUser })}
                                      data-testid={`button-support-conversations-${appUser.id}`}
                                      title={
                                        unread > 0
                                          ? `${unread} unread support message${unread === 1 ? "" : "s"}`
                                          : "View Crisp support conversations"
                                      }
                                    >
                                      <MessageSquare className="h-3.5 w-3.5 mr-1" />
                                      Support
                                      {unread > 0 && (
                                        <Badge
                                          variant="destructive"
                                          className="ml-1.5 h-5 min-w-5 justify-center rounded-full px-1.5 text-xs"
                                          data-testid={`badge-support-unread-${appUser.id}`}
                                        >
                                          {unread > 99 ? "99+" : unread}
                                        </Badge>
                                      )}
                                    </Button>
                                  );
                                })()}
                                {appUser.totpEnabled && (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => {
                                      const label = appUser.fullName || appUser.name || appUser.email;
                                      if (confirm(`Reset two-factor authentication for ${label}? They will be required to enrol again on next login.`)) {
                                        appUserResetTotpMutation.mutate(appUser.id);
                                      }
                                    }}
                                    disabled={appUserResetTotpMutation.isPending}
                                    data-testid={`button-reset-totp-app-user-${appUser.id}`}
                                    title="Clear this user's authenticator-app enrolment"
                                  >
                                    {appUserResetTotpMutation.isPending && appUserResetTotpMutation.variables === appUser.id
                                      ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                                      : <ShieldAlert className="h-3.5 w-3.5 mr-1" />}
                                    Reset 2FA
                                  </Button>
                                )}
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
                </>
              )}
            </Card>
          </TabsContent>

          <TabsContent value="founder-intents" data-tour="admin-founder-intents-section">
            <Card>
              <div className="flex flex-wrap items-center gap-2 p-4 border-b" data-testid="strip-founder-tier-summary" data-tour="admin-founder-intents-filters">
                <button
                  type="button"
                  onClick={() => setFounderTierFilter("all")}
                  className={`px-3 py-2 rounded-md border text-sm transition-colors ${
                    founderTierFilter === "all"
                      ? "bg-primary text-primary-foreground border-primary"
                      : "bg-card hover-elevate"
                  }`}
                  data-testid="button-founder-summary-all"
                >
                  <span className="font-semibold">All</span>
                  <span className="ml-2 text-xs opacity-80">
                    {founderIntentsAll.filter((i) => i.paidStatus === "paid").length} paid / {founderIntentsAll.length} total
                  </span>
                </button>
                {FOUNDER_TIERS.map((t) => {
                  const tierIntents = founderIntentsAll.filter((i) => i.tier === t);
                  const paidCount = tierIntents.filter((i) => i.paidStatus === "paid").length;
                  const active = founderTierFilter === t;
                  const tierClass =
                    t === "Founder25"
                      ? "border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-950/40 text-blue-900 dark:text-blue-200"
                      : t === "Founder50"
                        ? "border-purple-200 dark:border-purple-800 bg-purple-50 dark:bg-purple-950/40 text-purple-900 dark:text-purple-200"
                        : "border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/40 text-amber-900 dark:text-amber-200";
                  return (
                    <button
                      key={t}
                      type="button"
                      onClick={() => setFounderTierFilter(t)}
                      className={`px-3 py-2 rounded-md border text-sm transition-colors hover-elevate ${
                        active ? "ring-2 ring-primary " : ""
                      }${tierClass}`}
                      data-testid={`button-founder-summary-${t}`}
                    >
                      <span className="font-semibold">{t}</span>
                      <span className="ml-2 text-xs" data-testid={`text-founder-summary-${t}`}>
                        {paidCount} paid / {tierIntents.length} total
                      </span>
                    </button>
                  );
                })}
              </div>
              <div className="flex flex-wrap items-center gap-3 p-4 border-b">
                <div className="flex items-center gap-2">
                  <Label className="text-sm">Tier:</Label>
                  <Select value={founderTierFilter} onValueChange={setFounderTierFilter}>
                    <SelectTrigger className="w-40" data-testid="select-founder-tier-filter">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All tiers ({founderIntentsAll.length})</SelectItem>
                      {FOUNDER_TIERS.map((t) => (
                        <SelectItem key={t} value={t}>
                          {t} ({founderTierCounts[t as FounderTier] ?? 0}) — {FOUNDER_TIER_FREE_ROOMS[t as FounderTier]} rooms · {formatUsd(FOUNDER_TIER_PRICE_USD[t as FounderTier])}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center gap-2">
                  <Label className="text-sm">Paid:</Label>
                  <Select value={founderPaidFilter} onValueChange={(v) => setFounderPaidFilter(v as "all" | "paid" | "unpaid")}>
                    <SelectTrigger className="w-36" data-testid="select-founder-paid-filter">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All statuses</SelectItem>
                      <SelectItem value="paid">Paid only</SelectItem>
                      <SelectItem value="unpaid">Unpaid only</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setFounderSortDir((d) => (d === "desc" ? "asc" : "desc"))}
                  data-testid="button-toggle-founder-sort"
                >
                  <ArrowUpDown className="h-4 w-4 mr-1" />
                  Order: {founderSortDir === "asc" ? "Registration (oldest first)" : "Newest first"}
                </Button>
                <div className="flex flex-wrap items-center gap-1.5 ml-auto" data-testid="founder-tier-breakdown">
                  <Badge variant="outline" className="text-xs" data-testid="badge-tier-count-Founder25">
                    Founder 25: {founderTierCounts.Founder25} signups · {founderTierClicks.Founder25} clicks · {formatConversion(founderTierCounts.Founder25, founderTierClicks.Founder25)}
                  </Badge>
                  <Badge variant="outline" className="text-xs" data-testid="badge-tier-count-Founder50">
                    Founder 50: {founderTierCounts.Founder50} signups · {founderTierClicks.Founder50} clicks · {formatConversion(founderTierCounts.Founder50, founderTierClicks.Founder50)}
                  </Badge>
                  <Badge variant="outline" className="text-xs" data-testid="badge-tier-count-Founder100">
                    Founder 100: {founderTierCounts.Founder100} signups · {founderTierClicks.Founder100} clicks · {formatConversion(founderTierCounts.Founder100, founderTierClicks.Founder100)}
                  </Badge>
                  <p className="text-sm text-muted-foreground ml-2" data-testid="text-founder-intents-count">
                    Showing {founderIntents.length} of {founderIntentsAll.length}
                  </p>
                </div>
              </div>
              {founderIntentsQuery.isLoading ? (
                <div className="p-6 space-y-3">
                  {[1, 2, 3].map((i) => (
                    <Skeleton key={i} className="h-12 w-full" />
                  ))}
                </div>
              ) : founderIntents.length === 0 ? (
                <div className="p-12 text-center">
                  <Crown className="mx-auto h-10 w-10 text-muted-foreground/40 mb-3" />
                  <p className="text-muted-foreground">No founder signups yet.</p>
                  <p className="text-sm text-muted-foreground/70">
                    Reservations submitted on /founders-circle will appear here.
                  </p>
                </div>
              ) : (
                <div className="overflow-x-auto" data-tour="admin-founder-intents-table">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-12">#</TableHead>
                        <TableHead>Registered</TableHead>
                        <TableHead>Name</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead>Tier</TableHead>
                        <TableHead>Method</TableHead>
                        <TableHead className="w-28">Paid</TableHead>
                        <TableHead>Transaction link</TableHead>
                        <TableHead>Access granted</TableHead>
                        <TableHead className="text-right">Action</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {founderIntents.map((intent, idx) => {
                        const isActivated = !!intent.activatedAt;
                        const isGranting = activateFounderMutation.isPending && activateFounderMutation.variables === intent.id;
                        const paid = intent.paidStatus === "paid";
                        const togglingPayment = markFounderPaidMutation.isPending && markFounderPaidMutation.variables?.id === intent.id;
                        const savingRef = updateFounderReferenceMutation.isPending && updateFounderReferenceMutation.variables?.id === intent.id;
                        const refDraft = founderRefDrafts[intent.id] ?? (intent.paymentReference ?? "");
                        const refDirty = refDraft !== (intent.paymentReference ?? "");
                        const method = (intent as any).paymentMethod as "invoice" | "card" | undefined;
                        const paidSource = intent.paidSource as "mercury" | "manual" | null;
                        const paidAtRaw = intent.paidAt as unknown as string | null;
                        const latestPaidEvent = intent.latestPaidEvent;
                        const paidActorLabel = latestPaidEvent?.source === "mercury"
                          ? "Mercury automation"
                          : (latestPaidEvent?.adminLabel ?? null);
                        return (
                          <TableRow key={intent.id} data-testid={`row-founder-intent-${intent.id}`}>
                            <TableCell className="text-muted-foreground text-xs">
                              {founderSortDir === "asc" ? idx + 1 : founderIntents.length - idx}
                            </TableCell>
                            <TableCell className="text-muted-foreground text-sm whitespace-nowrap" data-testid={`text-founder-registered-${intent.id}`}>
                              {formatDate(intent.createdAt as unknown as string)}
                            </TableCell>
                            <TableCell className="font-medium" data-testid={`text-founder-name-${intent.id}`}>
                              {(intent as any).fullName || <span className="text-muted-foreground">—</span>}
                            </TableCell>
                            <TableCell data-testid={`text-founder-email-${intent.id}`}>
                              {intent.email}
                            </TableCell>
                            <TableCell>
                              {(() => {
                                const tierKey = intent.tier as FounderTier;
                                const isKnown = (FOUNDER_TIERS as string[]).includes(intent.tier);
                                const tierClass =
                                  tierKey === "Founder25"
                                    ? "bg-blue-100 dark:bg-blue-900/40 text-blue-800 dark:text-blue-200 border-blue-200 dark:border-blue-800 hover:bg-blue-100 dark:hover:bg-blue-900/60"
                                    : tierKey === "Founder50"
                                      ? "bg-purple-100 dark:bg-purple-900/50 text-purple-800 dark:text-purple-200 border-purple-200 dark:border-purple-800 hover:bg-purple-100 dark:hover:bg-purple-900/60"
                                      : tierKey === "Founder100"
                                        ? "bg-amber-100 dark:bg-amber-900/50 text-amber-800 dark:text-amber-200 border-amber-200 dark:border-amber-800 hover:bg-amber-100 dark:hover:bg-amber-900/60"
                                        : "";
                                return (
                                  <div className="flex flex-col gap-0.5">
                                    <Badge
                                      variant="outline"
                                      className={tierClass}
                                      data-testid={`badge-founder-tier-${intent.id}`}
                                    >
                                      {intent.tier}
                                    </Badge>
                                    {isKnown && (
                                      <span
                                        className="text-[10px] text-muted-foreground whitespace-nowrap"
                                        data-testid={`text-founder-tier-meta-${intent.id}`}
                                      >
                                        {FOUNDER_TIER_FREE_ROOMS[tierKey]} rooms · {formatUsd(FOUNDER_TIER_PRICE_USD[tierKey])}
                                      </span>
                                    )}
                                  </div>
                                );
                              })()}
                            </TableCell>
                            <TableCell className="text-xs" data-testid={`text-founder-method-${intent.id}`}>
                              {method === "card"
                                ? <Badge variant="outline">Card (+3%)</Badge>
                                : <Badge variant="outline">ACH invoice</Badge>}
                            </TableCell>
                            <TableCell>
                              <div className="flex flex-col gap-1">
                                <div className="flex items-center gap-2">
                                  <Switch
                                    checked={paid}
                                    disabled={togglingPayment}
                                    onCheckedChange={(checked) => markFounderPaidMutation.mutate({ id: intent.id, paid: checked })}
                                    data-testid={`switch-founder-paid-${intent.id}`}
                                  />
                                  <span className="text-xs text-muted-foreground" data-testid={`badge-founder-paid-${intent.id}`}>
                                    {paid ? "Paid" : "Unpaid"}
                                  </span>
                                </div>
                                {paid && (
                                  <>
                                    {paidSource === "mercury" ? (
                                      <Badge
                                        variant="outline"
                                        className="w-fit text-[10px] px-1.5 py-0 h-4 font-normal bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-200 border-green-200 dark:border-green-800"
                                        data-testid={`badge-founder-paid-source-${intent.id}`}
                                      >
                                        Auto · Mercury
                                      </Badge>
                                    ) : paidSource === "manual" ? (
                                      <Badge
                                        variant="outline"
                                        className="w-fit text-[10px] px-1.5 py-0 h-4 font-normal text-muted-foreground"
                                        data-testid={`badge-founder-paid-source-${intent.id}`}
                                      >
                                        {paidActorLabel ? `Manual · ${paidActorLabel}` : "Manual"}
                                      </Badge>
                                    ) : (
                                      <Badge
                                        variant="outline"
                                        className="w-fit text-[10px] px-1.5 py-0 h-4 font-normal text-muted-foreground"
                                        data-testid={`badge-founder-paid-source-${intent.id}`}
                                      >
                                        —
                                      </Badge>
                                    )}
                                    {paidAtRaw && (
                                      <span
                                        className="text-[10px] text-muted-foreground whitespace-nowrap"
                                        data-testid={`text-founder-paid-at-${intent.id}`}
                                      >
                                        {formatDate(paidAtRaw)}
                                      </span>
                                    )}
                                  </>
                                )}
                                {latestPaidEvent && (
                                  <button
                                    type="button"
                                    className="flex w-fit items-center gap-1 text-[10px] text-muted-foreground hover:text-foreground"
                                    onClick={() => setFounderPaidHistoryDialog({ open: true, intent })}
                                    data-testid={`button-founder-paid-history-${intent.id}`}
                                  >
                                    <History className="h-3 w-3" />
                                    History
                                  </button>
                                )}
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-1.5">
                                <Input
                                  value={refDraft}
                                  onChange={(e) => setFounderRefDrafts((prev) => ({ ...prev, [intent.id]: e.target.value }))}
                                  placeholder="Mercury / Stripe URL"
                                  className="h-8 text-xs min-w-[180px]"
                                  data-testid={`input-founder-ref-${intent.id}`}
                                />
                                {refDirty && (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="h-8 px-2"
                                    disabled={savingRef}
                                    onClick={() => updateFounderReferenceMutation.mutate({
                                      id: intent.id,
                                      paymentReference: refDraft.trim() ? refDraft.trim() : null,
                                    })}
                                    data-testid={`button-save-founder-ref-${intent.id}`}
                                  >
                                    {savingRef ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Save"}
                                  </Button>
                                )}
                                {!refDirty && intent.paymentReference && (() => {
                                  const ref = intent.paymentReference;
                                  const isStripePI = /^pi_/i.test(ref);
                                  const isMercuryURL = /^https?:\/\//i.test(ref);
                                  if (!isStripePI && !isMercuryURL) return null;
                                  const href = isStripePI
                                    ? `https://dashboard.stripe.com/payments/${ref}`
                                    : ref;
                                  return (
                                    <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
                                      <a
                                        href={href}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-xs text-blue-600 hover:underline"
                                        data-testid={`link-founder-ref-${intent.id}`}
                                      >
                                        {isStripePI ? "Stripe" : "Open"}
                                      </a>
                                      <Badge
                                        variant="outline"
                                        className="text-[10px] px-1.5 py-0 h-4 font-normal text-muted-foreground"
                                        data-testid={`badge-founder-payment-method-${intent.id}`}
                                      >
                                        {isStripePI ? "Card" : "Wire"}
                                      </Badge>
                                    </span>
                                  );
                                })()}
                              </div>
                            </TableCell>
                            <TableCell className="text-muted-foreground text-xs whitespace-nowrap" data-testid={`text-founder-access-${intent.id}`}>
                              {(intent as any).accessGrantedAt
                                ? formatDate((intent as any).accessGrantedAt as unknown as string)
                                : "—"}
                            </TableCell>
                            <TableCell className="text-right">
                              {isActivated ? (
                                <Badge variant="outline" data-testid={`text-founder-activated-${intent.id}`}>Access granted</Badge>
                              ) : (
                                <Button
                                  size="sm"
                                  onClick={() => setFounderToActivate(intent)}
                                  disabled={activateFounderMutation.isPending || !paid}
                                  title={!paid ? "Mark as paid before granting access" : undefined}
                                  data-testid={`button-grant-access-${intent.id}`}
                                  data-tour="admin-founder-intents-grant-access"
                                >
                                  {isGranting ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : null}
                                  Grant access
                                </Button>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              )}
            </Card>
          </TabsContent>





          <TabsContent value="explainer-videos" data-tour="admin-explainer-videos-section">
            <Card className="p-6 mb-4">
              <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
                <div>
                  <h2 className="text-lg font-semibold">Page Coverage</h2>
                  <p className="text-sm text-muted-foreground">Every page in the app that supports an explainer video. Missing ones can be created in one click.</p>
                </div>
                <div className="flex items-center gap-3 flex-wrap">
                  <div className="text-sm text-muted-foreground" data-testid="text-coverage-summary">
                    {(() => {
                      const configured = new Set((explainerVideosQuery.data || []).map((v) => v.pageKey));
                      const total = EXPLAINER_VIDEO_KEYS.length;
                      const have = EXPLAINER_VIDEO_KEYS.filter((k) => configured.has(k.key)).length;
                      return `${have} / ${total} configured`;
                    })()}
                  </div>
                  {(() => {
                    const configured = new Set((explainerVideosQuery.data || []).map((v) => v.pageKey));
                    const unconfigured = EXPLAINER_VIDEO_KEYS.filter((k) => !configured.has(k.key));
                    if (unconfigured.length === 0) return null;
                    return (
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={bulkQueueDraftsMutation.isPending}
                        onClick={() => bulkQueueDraftsMutation.mutate(unconfigured.map((k) => ({ key: k.key, label: k.label })))}
                        data-testid="button-bulk-queue-drafts"
                      >
                        {bulkQueueDraftsMutation.isPending ? "Queuing…" : `Queue all ${unconfigured.length} as Draft`}
                      </Button>
                    );
                  })()}
                </div>
              </div>
              {(() => {
                const configured = new Set((explainerVideosQuery.data || []).map((v) => v.pageKey));
                const groups: Record<string, typeof EXPLAINER_VIDEO_KEYS> = {};
                for (const k of EXPLAINER_VIDEO_KEYS) {
                  (groups[k.group] ||= []).push(k);
                }
                return (
                  <div className="space-y-4">
                    {Object.entries(groups).map(([group, items]) => (
                      <div key={group}>
                        <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">{group}</h3>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                          {items.map((it) => {
                            const has = configured.has(it.key);
                            return (
                              <div
                                key={it.key}
                                className={`flex items-center justify-between gap-3 px-3 py-2 rounded-md border ${has ? "bg-green-50 dark:bg-green-950/40 border-green-200 dark:border-green-800" : "bg-amber-50 dark:bg-amber-950/40 border-amber-200 dark:border-amber-800"}`}
                                data-testid={`row-coverage-${it.key}`}
                              >
                                <div className="min-w-0">
                                  <div className="text-sm font-medium truncate">{it.label}</div>
                                  <div className="font-mono text-xs text-muted-foreground truncate">{it.key}</div>
                                </div>
                                {has ? (
                                  <Badge variant="secondary" className="bg-green-100 dark:bg-green-900/50 text-green-800 dark:text-green-200 border-green-200 dark:border-green-800">Configured</Badge>
                                ) : (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => openCreateVideo(it.key, `How ${it.label} works`)}
                                    data-testid={`button-add-coverage-${it.key}`}
                                  >
                                    <Plus className="h-3.5 w-3.5 mr-1" />
                                    Add
                                  </Button>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                );
              })()}
            </Card>
            <Card className="p-6">
              <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
                <h2 className="text-lg font-semibold">Explainer Videos</h2>
                <div className="flex items-center gap-2 flex-wrap">
                  <Input
                    type="search"
                    placeholder="Search by page key or title..."
                    value={videoSearch}
                    onChange={(e) => setVideoSearch(e.target.value)}
                    className="h-8 w-64 text-sm"
                    data-testid="input-search-explainer-videos"
                  />
                  <Label htmlFor="filter-video-section" className="text-sm text-muted-foreground">Filter:</Label>
                  <Select value={videoSectionFilter} onValueChange={(v) => setVideoSectionFilter(v)}>
                    <SelectTrigger id="filter-video-section" className="h-8 w-48 text-xs" data-testid="select-filter-video-section">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All sections</SelectItem>
                      {videoSectionGroups.map((g) => (
                        <SelectItem key={g} value={g}>{g}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select value={videoStatusFilter} onValueChange={(v) => setVideoStatusFilter(v)}>
                    <SelectTrigger id="filter-video-status" className="h-8 w-40 text-xs" data-testid="select-filter-video-status">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All statuses</SelectItem>
                      <SelectItem value="draft">Rough Draft only</SelectItem>
                      <SelectItem value="pending">Pending only</SelectItem>
                      <SelectItem value="final">Final only</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button size="sm" onClick={() => openCreateVideo()} data-testid="button-add-explainer-video">
                    <Plus className="h-4 w-4 mr-1" />
                    Add Video
                  </Button>
                </div>
              </div>
              {explainerVideosQuery.isLoading ? (
                <div className="space-y-3">
                  {[1, 2, 3].map((i) => <Skeleton key={i} className="h-12 w-full" />)}
                </div>
              ) : (explainerVideosQuery.data || []).length === 0 ? (
                <div className="py-12 text-center">
                  <Video className="mx-auto h-10 w-10 text-muted-foreground/40 mb-3" />
                  <p className="text-muted-foreground">No explainer videos configured yet.</p>
                  <p className="text-sm text-muted-foreground/70 mt-1">Add a video to display it on a specific page.</p>
                </div>
              ) : (() => {
                const keyMap = new Map(EXPLAINER_VIDEO_KEYS.map((k) => [k.key, k]));
                const search = videoSearch.trim().toLowerCase();
                const filtered = (explainerVideosQuery.data || []).filter((v) => {
                  if (videoStatusFilter !== "all" && (v.status || "draft") !== videoStatusFilter) return false;
                  if (videoSectionFilter !== "all" && (keyMap.get(v.pageKey)?.group ?? null) !== videoSectionFilter) return false;
                  if (search && !String(v.pageKey ?? "").toLowerCase().includes(search) && !String(v.title ?? "").toLowerCase().includes(search)) return false;
                  return true;
                });
                const sorted = [...filtered].sort((a, b) => {
                  const dir = videoSort.direction === "asc" ? 1 : -1;
                  if (videoSort.column === "status") {
                    return ((a.status || "draft").localeCompare(b.status || "draft")) * dir;
                  }
                  const aT = a.updatedAt ? new Date(a.updatedAt).getTime() : 0;
                  const bT = b.updatedAt ? new Date(b.updatedAt).getTime() : 0;
                  return (aT - bT) * dir;
                });
                const toggleSort = (column: "status" | "updatedAt") => {
                  setVideoSort((prev) => prev.column === column
                    ? { column, direction: prev.direction === "asc" ? "desc" : "asc" }
                    : { column, direction: column === "updatedAt" ? "desc" : "asc" });
                };
                const sortIndicator = (column: "status" | "updatedAt") => {
                  if (videoSort.column !== column) return <ArrowUpDown className="h-3 w-3 ml-1 inline opacity-50" />;
                  return <ArrowUpDown className="h-3 w-3 ml-1 inline" />;
                };
                return (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Page Key</TableHead>
                        <TableHead>Section</TableHead>
                        <TableHead className="w-48">Page URL</TableHead>
                        <TableHead>Title</TableHead>
                        <TableHead>Embed URL</TableHead>
                        <TableHead className="w-16 text-center">YouTube</TableHead>
                        <TableHead className="w-36">
                          <button
                            type="button"
                            onClick={() => toggleSort("status")}
                            className="inline-flex items-center hover:text-foreground"
                            data-testid="button-sort-status"
                          >
                            Status{sortIndicator("status")}
                          </button>
                        </TableHead>
                        <TableHead className="w-40">
                          <button
                            type="button"
                            onClick={() => toggleSort("updatedAt")}
                            className="inline-flex items-center hover:text-foreground"
                            data-testid="button-sort-updated-at"
                          >
                            Last Updated{sortIndicator("updatedAt")}
                          </button>
                        </TableHead>
                        <TableHead className="w-24">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {sorted.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={9} className="text-center text-muted-foreground py-8" data-testid="text-no-filtered-videos">
                            No videos match this filter.
                          </TableCell>
                        </TableRow>
                      ) : sorted.map((v) => {
                        const keyEntry = keyMap.get(v.pageKey);
                        const pagePath = keyEntry?.path ?? null;
                        const isDynamic = pagePath ? pagePath.includes(":") : false;
                        return (
                        <TableRow key={v.id} data-testid={`row-explainer-video-${v.id}`}>
                          <TableCell className="font-mono text-sm" data-testid={`text-page-key-${v.id}`}>{v.pageKey}</TableCell>
                          <TableCell className="text-sm" data-testid={`text-section-${v.id}`}>{keyEntry?.group ?? <span className="text-muted-foreground">—</span>}</TableCell>
                          <TableCell className="text-sm max-w-[12rem]" data-testid={`text-page-url-${v.id}`}>
                            {pagePath ? (
                              isDynamic ? (
                                <code className="text-xs bg-muted px-1 py-0.5 rounded truncate block">{pagePath}</code>
                              ) : (
                                <a href={pagePath} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline text-xs truncate block">{pagePath}</a>
                              )
                            ) : (
                              <span className="text-muted-foreground">—</span>
                            )}
                          </TableCell>
                          <TableCell data-testid={`text-video-title-${v.id}`}>{v.title}</TableCell>
                          <TableCell className="max-w-xs">
                            {v.embedUrl?.trim() ? (
                              <a href={v.embedUrl.trim()} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline text-sm truncate block" data-testid={`link-embed-url-${v.id}`}>
                                {v.embedUrl.trim()}
                              </a>
                            ) : (
                              <span className="text-muted-foreground text-sm" data-testid={`link-embed-url-${v.id}`}>—</span>
                            )}
                          </TableCell>
                          <TableCell className="text-center" data-testid={`cell-youtube-${v.id}`}>
                            {v.youtubeUrl?.trim() ? (
                              <a href={v.youtubeUrl.trim()} target="_blank" rel="noopener noreferrer" aria-label="YouTube link" data-testid={`link-youtube-${v.id}`}>
                                <CheckCircle2 className="h-4 w-4 text-green-600 mx-auto" />
                              </a>
                            ) : (
                              <Minus className="h-4 w-4 text-muted-foreground/50 mx-auto" data-testid={`icon-youtube-missing-${v.id}`} />
                            )}
                          </TableCell>
                          <TableCell data-testid={`cell-status-${v.id}`}>
                            {(() => {
                              const normalizedStatus = ["draft", "pending", "final"].includes(v.status || "") ? (v.status || "draft") : "draft";
                              const badgeClass = normalizedStatus === "final"
                                ? "bg-green-100 text-green-800 border-green-200 hover:bg-green-100 dark:bg-green-900/40 dark:text-green-200 dark:border-green-800"
                                : normalizedStatus === "pending"
                                  ? "bg-gray-200 text-gray-800 border-gray-300 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-100 dark:border-gray-600"
                                  : "bg-yellow-100 text-yellow-800 border-yellow-200 hover:bg-yellow-100 dark:bg-yellow-900/40 dark:text-yellow-200 dark:border-yellow-800";
                              const badgeLabel = normalizedStatus === "final" ? "Final" : normalizedStatus === "pending" ? "Pending" : "Rough Draft";
                              return (
                                <div className="flex items-center gap-2">
                                  <Badge
                                    variant="outline"
                                    className={badgeClass}
                                    data-testid={`badge-status-${v.id}`}
                                  >
                                    {badgeLabel}
                                  </Badge>
                                  <Select
                                    value={normalizedStatus}
                                    onValueChange={(val) => patchVideoStatusMutation.mutate({ id: v.id, status: val })}
                                  >
                                    <SelectTrigger className="h-8 w-32 text-xs" data-testid={`select-status-${v.id}`}>
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="draft">Rough Draft</SelectItem>
                                      <SelectItem value="pending">Pending</SelectItem>
                                      <SelectItem value="final">Final</SelectItem>
                                    </SelectContent>
                                  </Select>
                                </div>
                              );
                            })()}
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground whitespace-nowrap" data-testid={`text-updated-at-${v.id}`}>
                            {v.updatedAt ? new Date(v.updatedAt).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" }) : "—"}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1">
                              <Button variant="ghost" size="icon" onClick={() => openEditVideo(v)} data-testid={`button-edit-video-${v.id}`}>
                                <Pencil className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                onClick={() => deleteVideoMutation.mutate(v.id)}
                                disabled={deleteVideoMutation.isPending}
                                data-testid={`button-delete-video-${v.id}`}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
                );
              })()}
            </Card>
          </TabsContent>

          <TabsContent value="gemini-usage" data-tour="admin-gemini-usage-section">
            <GeminiUsagePanel />
          </TabsContent>
          <TabsContent value="apify-usage" data-tour="admin-apify-usage-section">
            <ApifyUsagePanel />
          </TabsContent>
          <TabsContent value="elevenlabs-usage" data-tour="admin-elevenlabs-usage-section">
            <ElevenlabsUsagePanel />
          </TabsContent>
          <TabsContent value="ai-category-suggestions" data-tour="admin-ai-category-suggestions-section">
            <Card className="p-6">
              <AiCategorySuggestionsPanel />
            </Card>
          </TabsContent>
          <TabsContent value="sample-buy-deals" data-tour="admin-sample-buy-deals-section">
            <SampleBuyDealsPanel canManage={canManageMarketing} />
          </TabsContent>
          <TabsContent value="sale-listings" data-tour="admin-sale-listings-section">
            <SaleListingsAdminPanel canManage={canManageMarketing} />
          </TabsContent>
          <TabsContent value="secret-scan" data-tour="admin-secret-scan-section">
            <Card className="p-6">
              <SecretScanPanel />
            </Card>
          </TabsContent>

          <TabsContent value="encryption-audit" data-tour="admin-encryption-audit-section">
            <Card className="p-6">
              <EncryptionAuditPanel />
            </Card>
          </TabsContent>

          <TabsContent value="stripe-wipe" data-tour="admin-stripe-wipe-section">
            <Card className="p-6">
              <StripeWipeTestArtifactsPanel />
            </Card>
          </TabsContent>

          <TabsContent value="app-flags" data-tour="admin-app-flags-section">
            <Card className="p-6">
              <AppFlagsPanel />
            </Card>
          </TabsContent>

          <TabsContent value="blog" data-tour="admin-blog-section">
            <Card className="p-6">
              <BlogImportPanel />
            </Card>
          </TabsContent>

          <TabsContent value="funnel" data-tour="admin-funnel-section">
            <Card className="p-6">
              <FunnelPanel />
            </Card>
          </TabsContent>

          <TabsContent value="billing-v3" data-tour="admin-billing-v3-section">
            <Card className="p-6">
              <BillingV3Panel />
            </Card>
          </TabsContent>

          <TabsContent value="billing-errors" data-tour="admin-billing-errors-section">
            <Card className="p-6">
              <BillingErrorsPanel />
            </Card>
          </TabsContent>

          <TabsContent value="founder-overages" data-tour="admin-founder-overages-section">
            <Card className="p-6">
              <FounderOveragesPanel />
            </Card>
          </TabsContent>

          <TabsContent value="message-attachments" data-tour="admin-message-attachments-section">
            <Card className="p-6">
              <MessageAttachmentStoragePanel />
            </Card>
          </TabsContent>

          <TabsContent value="city-ai-analyses" data-tour="admin-city-ai-analyses-section">
            <Card className="p-6">
              <CityAiAnalysesPanel canManage={canManageMarketing} />
            </Card>
          </TabsContent>

          <TabsContent value="market-research-pdf" data-tour="admin-market-research-pdf-section">
            <Card className="p-6 mb-4">
              <MarketAutoPullAdmin canManage={isSuperAdminView || adminPermissions.includes("upload_marketing_data")} />
            </Card>
            <Card className="p-6">
              <MarketResearchPdfImportPanel />
            </Card>
          </TabsContent>

          <TabsContent value="voice-audio-cache" data-tour="admin-voice-audio-cache-section">
            <Card className="p-6">
              <VoiceAudioCachePanel />
            </Card>
          </TabsContent>

          <TabsContent value="pulse-digest" data-tour="admin-pulse-digest-section">
            <div className="mb-4 flex items-center justify-between rounded-lg border border-brand/25 bg-brand-muted/30 px-4 py-3">
              <div>
                <p className="text-sm font-semibold text-foreground">Admin Pulse — Weekly Briefing</p>
                <p className="text-xs text-muted-foreground mt-0.5">7-day AI-generated summary of platform-wide signals, with snooze &amp; dismiss controls.</p>
              </div>
              <a
                href="/admin/pulse"
                className="inline-flex items-center gap-1.5 rounded-md border border-brand/40 bg-white dark:bg-card px-3 py-1.5 text-xs font-medium text-brand hover:bg-brand-muted/50 transition-colors"
                data-testid="link-admin-pulse-briefing"
              >
                View Briefing →
              </a>
            </div>
            <Card className="p-6">
              <PulseDigestHealthPanel />
            </Card>
          </TabsContent>

          <TabsContent value="pulse-feedback" data-tour="admin-pulse-feedback-section">
            <Card className="p-6">
              <PulseFeedbackPanel />
            </Card>
          </TabsContent>

          <TabsContent value="thermostat-poll-health" data-tour="admin-thermostat-poll-health-section">
            <Card className="p-6">
              <ThermostatPollHealthPanel />
            </Card>
          </TabsContent>

          <TabsContent value="messages-pending-reconcile" data-tour="admin-messages-pending-reconcile-section">
            <Card className="p-6">
              <MessagesPendingReconcileHealthPanel />
            </Card>
          </TabsContent>

          <TabsContent value="padsplit-send-health" data-tour="admin-padsplit-send-health-section">
            <Card className="p-6">
              <PadsplitSendHealthPanel />
            </Card>
          </TabsContent>

          <TabsContent value="room-harvest">
            <Card className="p-6"><RoomHarvestPanel /></Card>
          </TabsContent>

          <TabsContent value="audit-log-health" data-tour="admin-audit-log-health-section">
            <Card className="p-6">
              <AuditLogHealthPanel />
            </Card>
          </TabsContent>

          <TabsContent value="changelog" data-tour="admin-changelog-section">
            <Card className="p-6">
              <ChangeLogPanel />
            </Card>
          </TabsContent>

          <TabsContent value="tos-notifications" data-tour="admin-tos-notifications-section">
            <Card className="p-6">
              <TosNotificationsPanel />
            </Card>
          </TabsContent>

          <TabsContent value="tos-versions" data-tour="admin-tos-versions-section">
            <Card className="p-6">
              <TosVersionsPanel />
            </Card>
          </TabsContent>

          <TabsContent value="lenders-brokers" data-tour="admin-lenders-brokers-section">
            <LendersBrokersPanel />
          </TabsContent>

          <TabsContent value="referral-commissions" data-tour="admin-referral-commissions-section">
            <ReferralCommissionsPanel />
          </TabsContent>

          <TabsContent value="partners-applications" data-tour="admin-partners-applications-section">
            <PartnerApplicationsPanel />
          </TabsContent>

          <TabsContent value="partners-agreements" data-tour="admin-partners-agreements-section">
            <PartnerAgreementSubmissionsPanel />
          </TabsContent>

          <TabsContent value="partners-all" data-tour="admin-partners-all-section">
            <AllPartnersPanel />
          </TabsContent>

          <TabsContent value="partners-realtors" data-tour="admin-partners-realtors-section">
            <RealtorPartnersPanel />
          </TabsContent>

          <TabsContent value="partners-wholesalers" data-tour="admin-partners-wholesalers-section">
            <WholesalerPartnersPanel />
          </TabsContent>

          <TabsContent value="partners-messages" data-tour="admin-partners-messages-section">
            <AdminPartnerMessagesPanel />
          </TabsContent>

          <TabsContent value="partners-application-alerts" data-tour="admin-partners-application-alerts-section">
            <PartnerApplicationNotificationSettingsPanel />
          </TabsContent>

          <TabsContent value="payment-instructions" data-tour="admin-payment-instructions-section">
            <PaymentInstructionsPanel />
          </TabsContent>

          <TabsContent value="privacy-policy-notify" data-tour="admin-privacy-policy-notify-section">
            <Card className="p-6">
              <PrivacyPolicyNotifyPanel />
            </Card>
          </TabsContent>

          <TabsContent value="terms-privacy-notice" data-tour="admin-terms-privacy-notice-section">
            <Card className="p-6">
              <TermsPrivacyNoticePanel />
            </Card>
          </TabsContent>

          <TabsContent value="important-links" data-tour="admin-important-links-section">
            <Card className="p-6">
              <OpsDocsPanel />
            </Card>
          </TabsContent>

          <TabsContent value="admin-users">
            <AdminUsersPanel />
          </TabsContent>

          <TabsContent value="impersonation-log" data-tour="admin-impersonation-log-section">
            <ImpersonationLogPanel />
          </TabsContent>

          <TabsContent value="vendor-2fa" data-tour="admin-vendor-2fa-section">
            <VendorTotpResetPanel />
          </TabsContent>

          <TabsContent value="member-merge" data-tour="admin-member-merge-section">
            <MemberMergePanel />
          </TabsContent>

          <TabsContent value="vendor-email-reset" data-tour="admin-vendor-email-reset-section">
            <VendorEmailResetPanel />
          </TabsContent>

          <TabsContent value="assistant-passwords" data-tour="admin-assistant-passwords-section">
            <AssistantPasswordRevealPanel />
          </TabsContent>

          <TabsContent value="refer-host-referrers" data-tour="admin-refer-host-referrers-section">
            <ReferHostReferrersPanel />
          </TabsContent>

          <TabsContent value="refer-host-statements" data-tour="admin-refer-host-statements-section">
            <ReferHostStatementsPanel />
          </TabsContent>

          <TabsContent value="refer-host-accruals" data-tour="admin-refer-host-accruals-section">
            <ReferHostAccrualsPanel />
          </TabsContent>

          <TabsContent value="affiliate-payouts" data-tour="admin-affiliate-payouts-section">
            <AffiliatePayoutsPanel />
          </TabsContent>

          <TabsContent value="conference-promo" data-tour="admin-conference-promo-section">
            <ConferencePromoBannerPanel />
          </TabsContent>

          <TabsContent value="founding-access-date" data-tour="admin-founding-access-date-section">
            <FoundingAccessDatePanel />
          </TabsContent>

          <TabsContent value="messaging-outage">
            <MessagingOutagePanel />
          </TabsContent>

          <TabsContent value="ai-reply-voice">
            <AiReplyVoicePanel />
          </TabsContent>

          <TabsContent value="churn-risk-alerts">
            <ChurnRiskAlertsPanel />
          </TabsContent>

          <TabsContent value="demo-sandbox" data-tour="admin-demo-sandbox-section">
            <DemoSandboxPanel />
          </TabsContent>

          <TabsContent value="beta-features" data-tour="admin-beta-features-section">
            <BetaFeaturesTab />
          </TabsContent>

          <TabsContent value="promo-codes" data-tour="admin-promo-codes-section">
            <PromoCodesTab />
          </TabsContent>

          <TabsContent value="menu-tier-locks" data-tour="admin-menu-tier-locks-section">
            <MenuTierLocksPanel />
          </TabsContent>

          <TabsContent value="rejected-tasks" data-tour="admin-rejected-tasks-section">
            <RejectedTasksPanel />
          </TabsContent>

          <TabsContent value="vendor-marketplace-fees" data-tour="admin-vendor-marketplace-fees-section">
            <MarketplaceFeesPanel />
          </TabsContent>
        </Tabs>
      </div>{/* closes px-4 py-8 sm:px-6 */}
        </main>{/* closes admin main content */}
      </div>{/* closes flex flex-1 body */}

      <Dialog
        open={messagingPhoneDialog.open}
        onOpenChange={(open) =>
          setMessagingPhoneDialog((d) => ({ ...d, open }))
        }
      >
        <DialogContent data-testid="dialog-edit-msg-phone">
          <DialogHeader>
            <DialogTitle>Set messaging phone number</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              For{" "}
              <strong>
                {messagingPhoneDialog.user?.fullName ||
                  messagingPhoneDialog.user?.name ||
                  messagingPhoneDialog.user?.email ||
                  "this user"}
              </strong>
              . Leave both fields blank to clear. This is an admin override and
              does not call Twilio.
            </p>
            <p className="text-xs text-muted-foreground" data-testid="text-msg-phone-test-mode-note">
              Note: the E2E_TWILIO_MOCK toggle that hands out fake "PNE2E…" Sids
              is only active in non-production environments. Production always
              provisions real Twilio numbers.
            </p>
            <div>
              <Label className="text-xs">Phone (E.164, e.g. +15551234567)</Label>
              <Input
                value={messagingPhoneDialog.phone}
                onChange={(e) =>
                  setMessagingPhoneDialog((d) => ({ ...d, phone: e.target.value }))
                }
                placeholder="+15551234567"
                data-testid="input-msg-phone"
              />
            </div>
            <div>
              <Label className="text-xs">Twilio Sid (optional)</Label>
              <Input
                value={messagingPhoneDialog.sid}
                onChange={(e) =>
                  setMessagingPhoneDialog((d) => ({ ...d, sid: e.target.value }))
                }
                placeholder="PNxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                data-testid="input-msg-phone-sid"
              />
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button
              variant="outline"
              onClick={() =>
                setMessagingPhoneDialog({ open: false, user: null, phone: "", sid: "" })
              }
              data-testid="button-msg-phone-cancel"
            >
              Cancel
            </Button>
            <Button
              onClick={() => {
                if (!messagingPhoneDialog.user) return;
                const phone = messagingPhoneDialog.phone.trim();
                const sid = messagingPhoneDialog.sid.trim();
                messagingPhoneMutation.mutate({
                  id: messagingPhoneDialog.user.id,
                  phone: phone === "" ? null : phone,
                  sid: sid === "" ? null : sid,
                });
              }}
              disabled={messagingPhoneMutation.isPending}
              data-testid="button-msg-phone-save"
            >
              Save
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog
        open={appUserSuspendDialog.open}
        onOpenChange={(open) => setAppUserSuspendDialog((d) => ({ ...d, open }))}
      >
        <DialogContent data-testid="dialog-suspend-app-user">
          <DialogHeader>
            <DialogTitle>Suspend User</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              Suspending{" "}
              <strong>
                {appUserSuspendDialog.user?.fullName ||
                  appUserSuspendDialog.user?.name ||
                  appUserSuspendDialog.user?.email ||
                  "this user"}
              </strong>{" "}
              will immediately sign them out of every browser and device. They will not be
              able to sign back in until reinstated. We'll email them a notification with
              the reason below so they aren't confused when they're signed out.
            </p>
            <div>
              <Label className="text-xs">
                Reason <span className="text-destructive">*</span>
              </Label>
              <Textarea
                value={appUserSuspendDialog.reason}
                onChange={(e) =>
                  setAppUserSuspendDialog((d) => ({ ...d, reason: e.target.value }))
                }
                rows={3}
                className="mt-1"
                placeholder="Why are you suspending this user?"
                data-testid="input-suspend-app-user-reason"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setAppUserSuspendDialog({ open: false, user: null, reason: "" })}
              data-testid="button-cancel-suspend-app-user"
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={
                !appUserSuspendDialog.reason.trim() ||
                appUserStatusMutation.isPending ||
                !appUserSuspendDialog.user
              }
              onClick={() => {
                if (appUserSuspendDialog.user) {
                  appUserStatusMutation.mutate({
                    id: appUserSuspendDialog.user.id,
                    status: "suspended",
                    reason: appUserSuspendDialog.reason.trim(),
                  });
                }
              }}
              data-testid="button-confirm-suspend-app-user"
            >
              {appUserStatusMutation.isPending && (
                <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
              )}
              Suspend User
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Task #3447: Per-row impersonation re-auth dialog. */}
      <Dialog
        open={impersonateDialog.open}
        onOpenChange={(open) => {
          if (!open && impersonateMutation.isPending) return;
          setImpersonateDialog((d) =>
            open ? { ...d, open } : { open: false, user: null, password: "" }
          );
        }}
      >
        <DialogContent data-testid="dialog-impersonate-app-user">
          <DialogHeader>
            <DialogTitle>Impersonate User</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              You're about to start an impersonation session as{" "}
              <strong>
                {impersonateDialog.user?.fullName ||
                  impersonateDialog.user?.name ||
                  impersonateDialog.user?.email ||
                  "this user"}
              </strong>
              . You'll be logged into their account; every action is attributed to
              them and recorded in the Impersonation Log. Sessions expire after 2 hours.
            </p>
            <div>
              <Label className="text-xs">
                Confirm your password <span className="text-destructive">*</span>
              </Label>
              <Input
                type="password"
                value={impersonateDialog.password}
                onChange={(e) =>
                  setImpersonateDialog((d) => ({ ...d, password: e.target.value }))
                }
                className="mt-1"
                placeholder="Your admin password"
                autoComplete="current-password"
                data-testid="input-impersonate-password"
                onKeyDown={(e) => {
                  if (
                    e.key === "Enter" &&
                    impersonateDialog.user &&
                    impersonateDialog.password &&
                    !impersonateMutation.isPending
                  ) {
                    impersonateMutation.mutate({
                      targetUserId: impersonateDialog.user.id,
                      password: impersonateDialog.password,
                    });
                  }
                }}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setImpersonateDialog({ open: false, user: null, password: "" })}
              data-testid="button-cancel-impersonate"
            >
              Cancel
            </Button>
            <Button
              disabled={
                !impersonateDialog.password ||
                impersonateMutation.isPending ||
                !impersonateDialog.user
              }
              onClick={() => {
                if (impersonateDialog.user) {
                  impersonateMutation.mutate({
                    targetUserId: impersonateDialog.user.id,
                    password: impersonateDialog.password,
                  });
                }
              }}
              data-testid="button-confirm-impersonate"
            >
              {impersonateMutation.isPending && (
                <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
              )}
              Start Impersonation
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={appUserReinstateDialog.open}
        onOpenChange={(open) => setAppUserReinstateDialog((d) => ({ ...d, open }))}
      >
        <DialogContent data-testid="dialog-reinstate-app-user">
          <DialogHeader>
            <DialogTitle>Reinstate User</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              Reinstating{" "}
              <strong>
                {appUserReinstateDialog.user?.fullName ||
                  appUserReinstateDialog.user?.name ||
                  appUserReinstateDialog.user?.email ||
                  "this user"}
              </strong>{" "}
              will set their status back to active and let them sign in again. We'll
              email them a confirmation so they know they can log back in.
            </p>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setAppUserReinstateDialog({ open: false, user: null })}
              data-testid="button-cancel-reinstate-app-user"
            >
              Cancel
            </Button>
            <Button
              disabled={appUserStatusMutation.isPending || !appUserReinstateDialog.user}
              onClick={() => {
                if (appUserReinstateDialog.user) {
                  appUserStatusMutation.mutate({
                    id: appUserReinstateDialog.user.id,
                    status: "active",
                  });
                }
              }}
              data-testid="button-confirm-reinstate-app-user"
            >
              {appUserStatusMutation.isPending && (
                <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
              )}
              Reinstate User
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Task #1291: Admin override for subscription_status. */}
      <Dialog
        open={subStatusDialog.open}
        onOpenChange={(open) => setSubStatusDialog((d) => ({ ...d, open }))}
      >
        <DialogContent data-testid="dialog-edit-sub-status">
          <DialogHeader>
            <DialogTitle>Override Subscription Status</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              Setting subscription status for{" "}
              <strong>
                {subStatusDialog.user?.fullName ||
                  subStatusDialog.user?.name ||
                  subStatusDialog.user?.email ||
                  "this user"}
              </strong>
              . Use <code>comped</code> to grant non-Stripe paid access (e.g., manual onboarding).
              The change is recorded in the audit log with your reason.
            </p>
            <div>
              <Label htmlFor="sub-status-select">Subscription status</Label>
              <Select
                value={subStatusDialog.status}
                onValueChange={(v) =>
                  setSubStatusDialog((d) => ({ ...d, status: v as SubscriptionStatus }))
                }
              >
                <SelectTrigger
                  id="sub-status-select"
                  className="mt-1"
                  data-testid="select-sub-status"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SUBSCRIPTION_STATUSES.map((s) => (
                    <SelectItem key={s} value={s} data-testid={`option-sub-status-${s}`}>
                      {s}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="sub-status-reason">Reason (required)</Label>
              <Textarea
                id="sub-status-reason"
                value={subStatusDialog.reason}
                onChange={(e) =>
                  setSubStatusDialog((d) => ({ ...d, reason: e.target.value }))
                }
                rows={3}
                className="mt-1"
                placeholder="e.g., Comped manual onboarding for prod Pro user"
                data-testid="input-sub-status-reason"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() =>
                setSubStatusDialog({ open: false, user: null, status: "comped", reason: "" })
              }
              data-testid="button-cancel-sub-status"
            >
              Cancel
            </Button>
            <Button
              disabled={
                subscriptionStatusMutation.isPending ||
                !subStatusDialog.reason.trim() ||
                !subStatusDialog.user
              }
              onClick={() => {
                if (subStatusDialog.user) {
                  subscriptionStatusMutation.mutate({
                    id: subStatusDialog.user.id,
                    subscriptionStatus: subStatusDialog.status,
                    reason: subStatusDialog.reason.trim(),
                  });
                }
              }}
              data-testid="button-confirm-sub-status"
            >
              {subscriptionStatusMutation.isPending && (
                <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
              )}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Task #1298: Subscription history — full audit trail of every */}
      {/* subscription_status change for the selected user, newest first. */}
      <Dialog
        open={subHistoryDialog.open}
        onOpenChange={(open) => setSubHistoryDialog((d) => ({ ...d, open }))}
      >
        <DialogContent className="max-w-2xl" data-testid="dialog-sub-status-history">
          <DialogHeader>
            <DialogTitle>Subscription history</DialogTitle>
            <DialogDescription>
              Every subscription status change for{" "}
              <strong>
                {subHistoryDialog.user?.fullName ||
                  subHistoryDialog.user?.name ||
                  subHistoryDialog.user?.email ||
                  "this user"}
              </strong>
              , newest first.
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-[60vh] overflow-y-auto">
            {subHistoryQuery.isLoading ? (
              <div className="flex items-center justify-center py-8" data-testid="status-sub-history-loading">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              </div>
            ) : subHistoryQuery.isError ? (
              <p className="py-6 text-sm text-destructive" data-testid="text-sub-history-error">
                {parseErrorMessage(subHistoryQuery.error, "Failed to load history.")}
              </p>
            ) : (subHistoryQuery.data?.length ?? 0) === 0 ? (
              <p className="py-6 text-sm text-muted-foreground" data-testid="text-sub-history-empty">
                No subscription status changes have been recorded for this user yet.
              </p>
            ) : (
              <ol className="space-y-3" data-testid="list-sub-history">
                {subHistoryQuery.data?.map((evt) => (
                  <li
                    key={evt.id}
                    className="rounded-md border p-3 text-sm"
                    data-testid={`item-sub-history-${evt.id}`}
                  >
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <div className="font-semibold" data-testid={`text-sub-history-transition-${evt.id}`}>
                        {evt.previousStatus} → {evt.newStatus}
                      </div>
                      <Badge variant="outline" data-testid={`badge-sub-history-source-${evt.id}`}>
                        {evt.source}
                      </Badge>
                    </div>
                    <div className="mt-1 text-xs text-muted-foreground" data-testid={`text-sub-history-when-${evt.id}`}>
                      {formatDate(evt.createdAt as unknown as string)}
                    </div>
                    <div className="mt-2" data-testid={`text-sub-history-reason-${evt.id}`}>
                      <span className="text-muted-foreground">Reason:</span> {evt.reason}
                    </div>
                    <div className="mt-1 text-xs" data-testid={`text-sub-history-actor-${evt.id}`}>
                      <span className="text-muted-foreground">By:</span>{" "}
                      {evt.adminLabel || "Unknown admin"}
                    </div>
                  </li>
                ))}
              </ol>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setSubHistoryDialog({ open: false, user: null })}
              data-testid="button-close-sub-history"
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Task #4341: Per-feature usage breakdown for the selected user, so */}
      {/* support can see which sections a host actually uses (and which they */}
      {/* never touched) to drive onboarding outreach. */}
      <Dialog
        open={sectionActivityDialog.open}
        onOpenChange={(open) => setSectionActivityDialog((d) => ({ ...d, open }))}
      >
        <DialogContent className="max-w-2xl" data-testid="dialog-section-activity">
          <DialogHeader>
            <DialogTitle>Feature usage breakdown</DialogTitle>
            <DialogDescription>
              Which sections of the app{" "}
              <strong>
                {sectionActivityDialog.user?.fullName ||
                  sectionActivityDialog.user?.name ||
                  sectionActivityDialog.user?.email ||
                  "this user"}
              </strong>{" "}
              has used, by page hits (last 7 / 30 days and all-time).
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-[60vh] overflow-y-auto">
            {sectionActivityQuery.isLoading ? (
              <div className="flex items-center justify-center py-8" data-testid="status-section-activity-loading">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              </div>
            ) : sectionActivityQuery.isError ? (
              <p className="py-6 text-sm text-destructive" data-testid="text-section-activity-error">
                {parseErrorMessage(sectionActivityQuery.error, "Failed to load usage breakdown.")}
              </p>
            ) : (sectionActivityQuery.data?.sections.length ?? 0) === 0 ? (
              <p className="py-6 text-sm text-muted-foreground" data-testid="text-section-activity-empty">
                No tracked activity for this user yet.
              </p>
            ) : (() => {
              const data = sectionActivityQuery.data!;
              const total = data.totalHitsAll || 0;
              const onlyDashboard =
                data.sections.length === 1 &&
                data.sections[0].section === "dashboard";
              return (
                <div className="space-y-3" data-testid="list-section-activity">
                  {onlyDashboard && (
                    <div
                      className="rounded-md border border-orange-300 bg-orange-50 dark:border-orange-900 dark:bg-orange-950 p-3 text-sm text-orange-800 dark:text-orange-200"
                      data-testid="banner-dashboard-only"
                    >
                      This user has only ever visited the dashboard — a good candidate for onboarding outreach.
                    </div>
                  )}
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Section</TableHead>
                        <TableHead className="text-right">7d</TableHead>
                        <TableHead className="text-right">30d</TableHead>
                        <TableHead className="text-right">All-time</TableHead>
                        <TableHead className="text-right">Share</TableHead>
                        <TableHead className="text-right">Last used</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {data.sections.map((s) => {
                        const share = total > 0 ? Math.round((s.hitsAll / total) * 100) : 0;
                        const label = s.section.charAt(0).toUpperCase() + s.section.slice(1);
                        return (
                          <TableRow key={s.section} data-testid={`row-section-${s.section}`}>
                            <TableCell className="font-medium" data-testid={`text-section-name-${s.section}`}>{label}</TableCell>
                            <TableCell className="text-right tabular-nums" data-testid={`text-section-hits7-${s.section}`}>{s.hits7}</TableCell>
                            <TableCell className="text-right tabular-nums" data-testid={`text-section-hits30-${s.section}`}>{s.hits30}</TableCell>
                            <TableCell className="text-right tabular-nums" data-testid={`text-section-hitsall-${s.section}`}>{s.hitsAll}</TableCell>
                            <TableCell className="text-right tabular-nums text-muted-foreground" data-testid={`text-section-share-${s.section}`}>{share}%</TableCell>
                            <TableCell className="text-right text-muted-foreground text-xs whitespace-nowrap" data-testid={`text-section-lastused-${s.section}`}>{s.lastActive ?? "—"}</TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              );
            })()}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setSectionActivityDialog({ open: false, user: null })}
              data-testid="button-close-section-activity"
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Founder paid-status history — full append-only audit trail of every */}
      {/* paid/unpaid change for the selected founder signup, newest first. */}
      <Dialog
        open={founderPaidHistoryDialog.open}
        onOpenChange={(open) => setFounderPaidHistoryDialog((d) => ({ ...d, open }))}
      >
        <DialogContent className="max-w-2xl" data-testid="dialog-founder-paid-history">
          <DialogHeader>
            <DialogTitle>Paid status history</DialogTitle>
            <DialogDescription>
              Every paid/unpaid change for{" "}
              <strong>
                {founderPaidHistoryDialog.intent?.fullName ||
                  founderPaidHistoryDialog.intent?.email ||
                  "this signup"}
              </strong>
              , newest first.
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-[60vh] overflow-y-auto">
            {founderPaidHistoryQuery.isLoading ? (
              <div className="flex items-center justify-center py-8" data-testid="status-founder-paid-history-loading">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              </div>
            ) : founderPaidHistoryQuery.isError ? (
              <p className="py-6 text-sm text-destructive" data-testid="text-founder-paid-history-error">
                {parseErrorMessage(founderPaidHistoryQuery.error, "Failed to load history.")}
              </p>
            ) : (founderPaidHistoryQuery.data?.length ?? 0) === 0 ? (
              <p className="py-6 text-sm text-muted-foreground" data-testid="text-founder-paid-history-empty">
                No paid status changes have been recorded for this signup yet.
              </p>
            ) : (
              <ol className="space-y-3" data-testid="list-founder-paid-history">
                {founderPaidHistoryQuery.data?.map((evt) => (
                  <li
                    key={evt.id}
                    className="rounded-md border p-3 text-sm"
                    data-testid={`item-founder-paid-history-${evt.id}`}
                  >
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <div className="font-semibold" data-testid={`text-founder-paid-history-transition-${evt.id}`}>
                        {evt.previousStatus} → {evt.newStatus}
                      </div>
                      <Badge variant="outline" data-testid={`badge-founder-paid-history-source-${evt.id}`}>
                        {evt.source === "mercury" ? "Auto · Mercury" : "Manual"}
                      </Badge>
                    </div>
                    <div className="mt-1 text-xs text-muted-foreground" data-testid={`text-founder-paid-history-when-${evt.id}`}>
                      {formatDate(evt.createdAt as unknown as string)}
                    </div>
                    <div className="mt-1 text-xs" data-testid={`text-founder-paid-history-actor-${evt.id}`}>
                      <span className="text-muted-foreground">By:</span>{" "}
                      {evt.source === "mercury"
                        ? "Mercury automation"
                        : (evt.adminLabel || "Unknown admin")}
                    </div>
                  </li>
                ))}
              </ol>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setFounderPaidHistoryDialog({ open: false, intent: null })}
              data-testid="button-close-founder-paid-history"
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Task #2835: Read-only Crisp support conversation history. */}
      <CrispConversationsDialog
        open={crispDialog.open}
        user={crispDialog.user}
        onClose={() => setCrispDialog({ open: false, user: null })}
      />

      {/* Task #4334: 30-day churn-watch activity drill-down. */}
      <UserActivityDrilldownDialog
        userId={activityDrilldown?.userId ?? null}
        userLabel={activityDrilldown?.label ?? ""}
        onClose={() => setActivityDrilldown(null)}
      />

      <Dialog
        open={videoDialog.open}
        onOpenChange={(open) => {
          if (createVideoMutation.isPending || updateVideoMutation.isPending) return;
          if (!open) setVideoDialog({ open: false, editing: null });
        }}
      >
        <DialogContent
          data-testid="dialog-explainer-video"
          className="max-w-lg max-h-[90vh] overflow-y-auto"
          onInteractOutside={(e) => { if (createVideoMutation.isPending || updateVideoMutation.isPending) e.preventDefault(); }}
          onEscapeKeyDown={(e) => { if (createVideoMutation.isPending || updateVideoMutation.isPending) e.preventDefault(); }}
          onPointerDownOutside={(e) => { if (createVideoMutation.isPending || updateVideoMutation.isPending) e.preventDefault(); }}
        >
          <DialogHeader>
            <DialogTitle>{videoDialog.editing ? "Edit Explainer Video" : "Add Explainer Video"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleVideoSubmit} className="space-y-4">
            <div>
              <Label htmlFor="video-page-key">Page Key</Label>
              <Input
                id="video-page-key"
                placeholder="e.g. research, underwriting, dashboard"
                value={videoForm.pageKey}
                onChange={(e) => setVideoForm((f) => ({ ...f, pageKey: e.target.value }))}
                required
                className="mt-1"
                data-testid="input-video-page-key"
              />
              <p className="text-xs text-muted-foreground mt-1">Unique identifier for the page (e.g. "research", "properties", "maintenance")</p>
            </div>
            <div>
              <Label htmlFor="video-title">Title</Label>
              <Input
                id="video-title"
                placeholder="e.g. How to use Market Research"
                value={videoForm.title}
                onChange={(e) => setVideoForm((f) => ({ ...f, title: e.target.value }))}
                required
                className="mt-1"
                data-testid="input-video-title"
              />
            </div>
            <div>
              <Label htmlFor="video-embed-url">Loom Embed URL</Label>
              <Input
                id="video-embed-url"
                placeholder="https://www.loom.com/share/..."
                value={videoForm.embedUrl}
                onChange={(e) => setVideoForm((f) => ({ ...f, embedUrl: e.target.value }))}
                className="mt-1"
                data-testid="input-video-embed-url"
              />
              <p className="text-xs text-muted-foreground mt-1">Paste the Loom share or embed URL</p>
            </div>
            <div>
              <Label htmlFor="video-youtube-url">YouTube Link</Label>
              <Input
                id="video-youtube-url"
                placeholder="https://www.youtube.com/watch?v=..."
                value={videoForm.youtubeUrl}
                onChange={(e) => setVideoForm((f) => ({ ...f, youtubeUrl: e.target.value }))}
                className="mt-1"
                data-testid="input-video-youtube-url"
              />
              <p className="text-xs text-muted-foreground mt-1">Optional YouTube link for this explainer video</p>
            </div>
            <div>
              <Label htmlFor="video-status">Status</Label>
              <Select value={videoForm.status} onValueChange={(val) => setVideoForm((f) => ({ ...f, status: val as "draft" | "pending" | "final" }))}>
                <SelectTrigger id="video-status" className="mt-1" data-testid="select-video-status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="draft">Rough Draft</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="final">Final</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {videoDialog.editing && (
              <VideoEditHistory videoId={videoDialog.editing.id} />
            )}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setVideoDialog({ open: false, editing: null })} disabled={createVideoMutation.isPending || updateVideoMutation.isPending} data-testid="button-cancel-video">Cancel</Button>
              <Button type="submit" disabled={createVideoMutation.isPending || updateVideoMutation.isPending} data-testid="button-save-video">
                {(createVideoMutation.isPending || updateVideoMutation.isPending) && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />}
                {videoDialog.editing ? "Update" : "Save"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>



      {/* Task #2027: confirm dialog surfacing the tier fields before activation */}
      <AlertDialog open={!!founderToActivate} onOpenChange={(v) => !v && setFounderToActivate(null)}>
        <AlertDialogContent data-testid="dialog-grant-founder-access">
          <AlertDialogHeader>
            <AlertDialogTitle>Grant founder access?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3 text-sm">
                <p>
                  This will create or comp the account for{" "}
                  <span className="font-medium text-foreground" data-testid="text-confirm-founder-email">
                    {founderToActivate?.email}
                  </span>{" "}
                  and send the welcome email.
                </p>
                {founderToActivate && (FOUNDER_TIERS as string[]).includes(founderToActivate.tier) && (
                  <div className="rounded-md border bg-muted/50 p-3 space-y-1.5">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Tier</span>
                      <span className="font-medium text-foreground" data-testid="text-confirm-founder-tier">
                        {founderToActivate.tier}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Free rooms (lifetime)</span>
                      <span className="font-medium text-foreground" data-testid="text-confirm-founder-rooms">
                        {FOUNDER_TIER_FREE_ROOMS[founderToActivate.tier as FounderTier]}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Reservation price</span>
                      <span className="font-medium text-foreground" data-testid="text-confirm-founder-price">
                        {formatUsd(FOUNDER_TIER_PRICE_USD[founderToActivate.tier as FounderTier])}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Subscription status</span>
                      <span className="font-medium text-foreground">comped</span>
                    </div>
                  </div>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="button-cancel-grant-access">Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="button-confirm-grant-access"
              disabled={activateFounderMutation.isPending}
              onClick={(e) => {
                e.preventDefault();
                if (!founderToActivate) return;
                const id = founderToActivate.id;
                activateFounderMutation.mutate(id, {
                  onSettled: () => setFounderToActivate(null),
                });
              }}
            >
              {activateFounderMutation.isPending && <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />}
              Grant access
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export default function Admin() {
  usePageTitle("Admin");
  const [authenticated, setAuthenticated] = useState<boolean | null>(null);

  const authCheck = useQuery<{ authenticated: boolean; identity?: string | null }>({
    queryKey: ["/api/admin/check"],
    queryFn: async () => {
      const res = await fetch("/api/admin/check");
      return res.json();
    },
  });

  const adminIdentity = authCheck.data?.identity ?? null;

  const isAuth = authCheck.data?.authenticated || authenticated === true;

  if (authCheck.isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!isAuth) {
    return <AdminLogin onLogin={() => setAuthenticated(true)} />;
  }

  return (
    <AdminDashboard
      adminIdentity={adminIdentity}
      onLogout={() => {
        setAuthenticated(false);
        authCheck.refetch();
      }}
    />
  );
}
